import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, Box, Divider,
  Chip, Grid, Accordion, AccordionSummary, AccordionDetails,
  FormControl, InputLabel, Select, MenuItem, FormHelperText,
  CircularProgress, Alert, IconButton, Tooltip, LinearProgress,
  Stack, Paper, useMediaQuery, useTheme, Popover,
  Checkbox, FormControlLabel, FormGroup, Autocomplete,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ScienceIcon from '@mui/icons-material/Science';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import VerifiedIcon from '@mui/icons-material/Verified';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ResponsiveContainer, Legend,
} from 'recharts';
import AttachFileButton from './AttachFileButton';
import RichTextEditor, { RichTextView, sanitizeNotesHtml } from './RichTextEditor';
import {
  createMedicalRecord, updateMedicalRecord, getConsultationTemplates, getDoctors,
  getRecordFiles, uploadRecordFile, downloadRecordFile, deleteRecordFile, renameRecordFile,
  getPatientStudies, uploadPatientStudy, downloadPatientStudy, deletePatientStudy, updatePatientStudy,
  addFileToStudyGroup,
  getRecordStudies, uploadRecordStudy,
  updatePatient,
  getPatientProblems, createPatientProblem, updatePatientProblem, deletePatientProblem,
  getConsultationTypes, createConsultationType,
} from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useNotification } from '../hooks/useNotification';
import { useClinicFeatures } from '../hooks/useClinicFeatures';

// ── Utilidades ────────────────────────────────────────────────────────────

const FILE_ICON = (mime) => {
  if (!mime) return <InsertDriveFileIcon fontSize="small" />;
  if (mime.startsWith('image/')) return <ImageIcon fontSize="small" color="info" />;
  if (mime === 'application/pdf') return <PictureAsPdfIcon fontSize="small" color="error" />;
  return <InsertDriveFileIcon fontSize="small" color="action" />;
};

// Repetido antes en 4 lugares con la misma condición — imagen o PDF son los
// únicos tipos que el visor con carrusel (RecordFileViewer) sabe mostrar.
const isPreviewable = (mimeType) => mimeType?.startsWith('image/') || mimeType === 'application/pdf';

// Repetido antes en 6 lugares (descarga de archivo/estudio): mismo bloque
// blob → link temporal → click → revoke.
const downloadBlobAsFile = (blob, filename, mimeType) => {
  const url = URL.createObjectURL(new Blob([blob], { type: mimeType || 'application/octet-stream' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

// Reemplaza los window.confirm nativos de "eliminar X" — inconsistentes con
// el resto de la app (que ya usa Dialog de MUI, ver el de "sobreescribir
// plantilla" más abajo). onConfirm recibe el item pendiente; el caller solo
// necesita llamar open(item) para disparar la confirmación.
const useConfirmDialog = () => {
  const [pending, setPending] = useState(null);
  return {
    pending,
    open: (item) => setPending(item),
    close: () => setPending(null),
  };
};

// Lista de "Problemas" + sus acciones (editar/eliminar) — antes repetida
// casi línea por línea entre el modo compact y no-compact de ResumenTab
// (sidebar del paciente vs. perfil completo), con el riesgo de que un fix
// futuro se aplique a una copia y se olvide en la otra (ya pasó con el
// criterio de mostrar las acciones siempre en mobile).
const PROBLEM_DOT_COLORS = { activo: '#ef5350', cronico: '#ff9800', resuelto: '#9e9e9e' };

const ProblemsList = ({ problems, canManage, onAdd, onEdit, onDelete }) => (
  <Box>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
      <Typography variant="subtitle2" fontWeight={700} color="text.secondary"
        sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem' }}>
        Problemas
      </Typography>
      {canManage && (
        <IconButton size="small" onClick={onAdd} sx={{ p: 0.25 }}>
          <AddIcon sx={{ fontSize: 18 }} />
        </IconButton>
      )}
    </Box>
    {problems.length === 0 ? (
      <Typography variant="caption" color="text.disabled">Sin problemas registrados.</Typography>
    ) : (
      <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
        {problems.map(p => (
          <Box component="li" key={p.id} sx={{
            display: 'flex', alignItems: 'baseline', gap: 0.75, py: 0.4,
            borderBottom: '1px solid', borderColor: 'divider',
            '&:last-child': { borderBottom: 'none' },
            '&:hover .problem-actions': { opacity: 1 },
          }}>
            <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: PROBLEM_DOT_COLORS[p.status] || PROBLEM_DOT_COLORS.activo, flexShrink: 0, mt: '3px' }} />
            <Typography variant="body2" sx={{ flex: 1, lineHeight: 1.4 }} title={p.notes || undefined}>
              {p.name}
              {p.status === 'resuelto' && <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>(resuelto)</Typography>}
              {p.status === 'cronico' && <Typography component="span" variant="caption" color="warning.dark" sx={{ ml: 0.5 }}>crónico</Typography>}
            </Typography>
            {canManage && (
              // Sin hover en touch, las acciones quedaban inalcanzables en
              // mobile — visibles siempre en xs, solo al hover en desktop (sm+).
              <Box className="problem-actions" sx={{ opacity: { xs: 1, sm: 0 }, transition: 'opacity 0.15s', display: 'flex', gap: 0.25 }}>
                <IconButton size="small" sx={{ p: 0.25 }} onClick={() => onEdit(p)}><EditIcon sx={{ fontSize: 14 }} /></IconButton>
                <IconButton size="small" sx={{ p: 0.25 }} color="error" onClick={() => onDelete(p)}><DeleteIcon sx={{ fontSize: 14 }} /></IconButton>
              </Box>
            )}
          </Box>
        ))}
      </Box>
    )}
  </Box>
);

// Cuerpo de "Resumen clínico" (textarea editable + botón Actualizar, o
// texto de solo lectura) — antes repetido entre compact/no-compact con
// solo el placeholder/spacing distintos; cada caller sigue poniendo su
// propio título, que sí difiere en jerarquía entre ambos modos.
const ClinicalSummaryEditor = ({ canManage, summary, onChange, onSave, saving, dirty, placeholder }) => (
  canManage ? (
    <>
      <TextField fullWidth multiline rows={4} size="small" placeholder={placeholder}
        value={summary} onChange={e => onChange(e.target.value)} />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
        <Button size="small" variant="contained" onClick={onSave} disabled={saving || !dirty}>
          {saving ? <CircularProgress size={14} /> : 'Actualizar'}
        </Button>
      </Box>
    </>
  ) : (
    <Typography variant="body2" color={summary ? 'text.primary' : 'text.secondary'} sx={{ whiteSpace: 'pre-wrap' }}>
      {summary || 'Sin resumen clínico.'}
    </Typography>
  )
);

const ConfirmDialog = ({ open, title, message, onCancel, onConfirm, confirmLabel = 'Eliminar' }) => (
  <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
    <DialogTitle>{title}</DialogTitle>
    <DialogContent>
      <Typography variant="body2">{message}</Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onCancel}>Cancelar</Button>
      <Button variant="contained" color="error" onClick={onConfirm}>{confirmLabel}</Button>
    </DialogActions>
  </Dialog>
);

const formatBytes = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// `notes` puede tener HTML (negrita/color, ver RichTextEditor) desde que se
// agregó formato enriquecido — los listados solo necesitan el texto, nunca
// el markup. Convierte <div>/<p>/<br> a saltos de línea reales ANTES de
// quitar el resto de las tags, para no pegar bloques que estaban en líneas
// separadas (ej. "<div>Uno</div><div>Dos</div>" → "Uno\nDos", no "UnoDos").
const stripHtmlToText = (html) => {
  if (!html) return '';
  if (!/<[a-z][\s\S]*>/i.test(html)) return html; // texto plano de antes del rich text — nada que hacer
  return html
    .replace(/<\/(div|p)>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>');
};

// Resumen de una consulta para usar como "título" en listados (Resumen,
// Consultas). Antes se colapsaban TODOS los saltos de línea a espacios y se
// cortaba a 140 caracteres, pegando frases sin separación ("Esta es una
// evolucion.Eso fue un punto y a parte..."). Después se tomaba solo la
// primera línea/oración, pero cuando el HTML no traía el salto de línea
// esperado entre bloques (ej. contenido legado con \n crudo sin <p>/<div>,
// o un editor que no cerró el bloque como se esperaba) esa única "primera
// línea" terminaba siendo dos renglones reales pegados sin espacio
// ("deriva DR Machadotiene hecha...") — por eso nunca se confía en una sola
// línea ni se unen dos líneas reales sin separador explícito.
//
// Caso real reportado 2026-10-07: médicos que casi no usan plantillas
// escriben la evolución como una línea por dato ("28 años" / "Control del
// embarazo" / "9.5 semanas" / "G1" / "AP: obesidad"...). Unir todo con
// espacio y cortar a 160 caracteres daba un párrafo corrido e ilegible
// ("28 años Control del embarazo 9.5 semanas G1 AP: obesidad MH: no..."),
// aunque el médico SÍ había estructurado el dato línea por línea al
// escribir. Ahora se preservan las primeras líneas reales tal cual
// (join con '\n', no con espacio) hasta un máximo de líneas y de
// caracteres totales — se lee como la lista que en los hechos es, en vez
// de forzarla a un párrafo. Se sigue recortando duro por caracteres como
// red de seguridad (una sola línea gigante, sin saltos, no debe desbordar).
const recordSummary = (text, maxLen = 160, maxLines = 3) => {
  if (!text) return '';
  const plain = stripHtmlToText(text);
  const lines = plain.split('\n').map(l => l.trim()).filter(Boolean);
  const kept = lines.slice(0, maxLines);
  const truncatedByLines = lines.length > maxLines;
  let result = kept.join('\n');
  if (result.length > maxLen) {
    result = `${result.slice(0, maxLen).trim()}…`;
  } else if (truncatedByLines) {
    result += '…';
  }
  return result;
};

// El backend guarda created_at/finalized_at como naive-UTC (sin offset) —
// new Date("2026-09-14T18:30:00") sin la 'Z' lo interpreta el navegador
// como hora LOCAL, mostrando la consulta ~3hs adelantada en Argentina.
// Mismo criterio que ya se usa para start_time de turnos en el resto de
// la app (CalendarGrid, AppointmentSearch, etc.) — pedido explícito del
// usuario tras notar el desfasaje específicamente en la HC.
const _asUtcDate = (d) => new Date(typeof d === 'string' && !d.endsWith('Z') ? d + 'Z' : d);

const fmtDate = (d, opts = { day: '2-digit', month: '2-digit', year: 'numeric' }) =>
  d ? _asUtcDate(d).toLocaleDateString('es-AR', opts) : '—';

const fmtDateTime = (d) =>
  d ? _asUtcDate(d).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

// ── Constantes ────────────────────────────────────────────────────────────

const STUDY_CATEGORIES = {
  laboratorio: { label: 'Laboratorio', color: '#1565c0', bg: '#e3f2fd', icon: <ScienceIcon sx={{ fontSize: 16 }} /> },
  imagen:       { label: 'Imágenes',    color: '#6a1b9a', bg: '#f3e5f5', icon: <ImageIcon sx={{ fontSize: 16 }} /> },
  informe:      { label: 'Informe',     color: '#2e7d32', bg: '#e8f5e9', icon: <InsertDriveFileIcon sx={{ fontSize: 16 }} /> },
  derivacion:   { label: 'Derivación',  color: '#e65100', bg: '#fff3e0', icon: <MedicalServicesIcon sx={{ fontSize: 16 }} /> },
  otro:         { label: 'Otro',        color: '#546e7a', bg: '#f5f5f5', icon: <InsertDriveFileIcon sx={{ fontSize: 16 }} /> },
};

const VITAL_LABELS = {
  vital_weight_kg: 'Peso (kg)',
  vital_height_cm: 'Talla (cm)',
  vital_blood_pressure: 'TA',
  vital_heart_rate: 'FC (lpm)',
  vital_temperature_c: 'Temp (°C)',
  vital_oxygen_sat: 'SatO₂ (%)',
};


const emptyRecord = {
  consultation_type: '', notes: '',
  vital_weight_kg: '', vital_height_cm: '', vital_blood_pressure: '',
  vital_heart_rate: '', vital_temperature_c: '', vital_oxygen_sat: '',
  // Biometría fetal — feature nicho, solo se escribe si el flag
  // "obstetrics" está activo, pero vive en `form` como el resto de los
  // signos vitales para que se guarde automáticamente con el resto.
  vital_fetal_bpd_mm: '', vital_fetal_hc_mm: '', vital_fetal_ac_mm: '',
  vital_fetal_fl_mm: '', vital_fetal_efw_g: '',
  preeclampsia_risk_factors: '',
  // Edad gestacional/FPP de esta consulta puntual — snapshot, mismo
  // patrón que vital_fetal_efw_g (se sincroniza automáticamente desde el
  // resultado de la calculadora vía useEffect más abajo).
  gest_age_weeks: '', gest_age_days: '', edd_date: '',
};

// Caso real reportado: un médico tipeó la altura en METROS ("1.65") en vez
// de centímetros ("165") como pide el label "Talla (cm)" — el cálculo
// daba un IMC de cientos de miles ("430037.7 — Obesidad"), sin ningún
// aviso. Ninguna altura humana real cabe en 0.5-2.5 cm (sería un error de
// tipeo evidente), así que un valor crudo en ese rango se interpreta como
// "esto ya vino en metros por error" y se normaliza a cm antes de
// calcular — no se bloquea la carga, se corrige en silencio porque el
// caso real (confundir cm con m) es casi siempre la intención obvia.
const calcBMI = (weightKg, heightCm) => {
  const w = parseFloat(weightKg);
  let hRaw = parseFloat(heightCm);
  if (!w || !hRaw) return null;
  if (hRaw >= 0.5 && hRaw <= 2.5) hRaw = hRaw * 100; // tipeado en metros por error
  const h = hRaw / 100;
  if (h <= 0) return null;
  const bmi = w / (h * h);
  let label = bmi < 18.5 ? 'Bajo peso' : bmi < 25 ? 'Normal' : bmi < 30 ? 'Sobrepeso' : 'Obesidad';
  return { value: bmi.toFixed(1), label };
};

// EGA por CRL (longitud cráneo-caudal, mm) — fórmula de Robinson
const crlToGA = (crlMm) => {
  const crl = parseFloat(crlMm);
  if (!crl || crl <= 0 || crl > 84) return null;
  // days = 8.052 * sqrt(CRL + 23.73) + 23.73  (Robinson 1973, validado por AIUM)
  const days = Math.round(8.052 * Math.sqrt(crl + 23.73) + 23.73);
  return days;
};

const formatFPP = (date) =>
  date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });

// Convierte "dd/mm/yyyy" (como lo guarda formatFPP) a "yyyy-mm-dd" (lo que
// espera un <input type="date">) — edd_date se persiste ya formateado para
// mostrar, así que hay que volver a parsearlo para poder precargarlo en el
// campo de fecha de la calculadora.
const fppDisplayToIsoDate = (display) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(display || '');
  return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
};

// Busca en las consultas previas del paciente un embarazo todavía en curso
// (FPP guardada y no vencida) — pedido explícito del usuario: si ya se
// cargó el dato ancla (FUM/eco/CRL/FPP) en una consulta anterior del mismo
// embarazo, no tiene sentido volver a pedírselo al médico en cada control,
// el sistema ya "sabe" cuántas semanas lleva. `records` viene ordenado por
// fecha desc (más reciente primero), así que el primero con edd_date
// vigente es la referencia correcta. Un FPP ya vencido no se ofrece —lo
// más probable es que ese embarazo ya haya terminado.
const findOngoingPregnancy = (records) => {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  for (const r of records || []) {
    if (!r.edd_date) continue;
    const iso = fppDisplayToIsoDate(r.edd_date);
    if (!iso) continue;
    const eddDate = new Date(iso);
    if (eddDate >= today) return { edd: iso, eddDisplay: r.edd_date };
  }
  return null;
};

const calcEGAFromMethod = ({ method, fum, sonoDate, sonoWeeks, sonoDays, crl, edd }) => {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  if (method === 'fum') {
    if (!fum) return null;
    const fumDate = new Date(fum);
    const diffDays = Math.floor((today - fumDate) / 86400000);
    if (diffDays < 0 || diffDays > 300) return null;
    const fppDate = new Date(fumDate); fppDate.setDate(fppDate.getDate() + 280);
    return { weeks: Math.floor(diffDays / 7), days: diffDays % 7, fpp: formatFPP(fppDate) };
  }
  if (method === 'sono') {
    if (!sonoDate || sonoWeeks === '' || sonoWeeks == null) return null;
    const sono = new Date(sonoDate);
    const totalDaysAtSono = Number(sonoWeeks) * 7 + Number(sonoDays || 0);
    // EDD = fecha eco - días de EGA en eco + 280
    const eddDate = new Date(sono);
    eddDate.setDate(eddDate.getDate() - totalDaysAtSono + 280);
    const currentDays = Math.floor((today - sono) / 86400000) + totalDaysAtSono;
    if (currentDays < 0 || currentDays > 300) return null;
    return { weeks: Math.floor(currentDays / 7), days: currentDays % 7, fpp: formatFPP(eddDate) };
  }
  if (method === 'crl') {
    if (!sonoDate || !crl) return null;
    const sono = new Date(sonoDate);
    const daysAtSono = crlToGA(crl);
    if (!daysAtSono) return null;
    const eddDate = new Date(sono);
    eddDate.setDate(eddDate.getDate() - daysAtSono + 280);
    const currentDays = Math.floor((today - sono) / 86400000) + daysAtSono;
    if (currentDays < 0 || currentDays > 300) return null;
    return { weeks: Math.floor(currentDays / 7), days: currentDays % 7, fpp: formatFPP(eddDate) };
  }
  if (method === 'edd') {
    if (!edd) return null;
    const eddDate = new Date(edd);
    const conceptionDate = new Date(eddDate); conceptionDate.setDate(conceptionDate.getDate() - 280);
    const diffDays = Math.floor((today - conceptionDate) / 86400000);
    if (diffDays < 0 || diffDays > 300) return null;
    return { weeks: Math.floor(diffDays / 7), days: diffDays % 7, fpp: formatFPP(eddDate) };
  }
  return null;
};

// ── Obstetricia: biometría fetal y riesgo de preeclampsia ──────────────────
// Feature nicho (solo gineco-obstetricia la usa) — detrás del feature flag
// "obstetrics", pedido explícito de un médico que compartió otro software
// (Astraia) como referencia. Peso estimado con la fórmula Hadlock IV (la
// más citada en biometría fetal) y percentilo interpolado contra la tabla
// de referencia poblacional Mikolajczyk et al., Lancet 2011 (adapta
// Hadlock 1991) — NO es el algoritmo propietario de la Fetal Medicine
// Foundation (FMF), que requiere licencia; esto es una aproximación de uso
// general, aclarado también en la UI.

// EFW (g) = 10^(1.3596 + 0.0064·HC + 0.0424·AC + 0.174·FL + 0.00061·BPD·AC − 0.00386·AC·FL)
// HC/AC/BPD/FL en cm — Hadlock FP et al., Radiology 1985;151(2):333-7.
const calcEFW = ({ bpd, hc, ac, fl }) => {
  const BPD = parseFloat(bpd), HC = parseFloat(hc), AC = parseFloat(ac), FL = parseFloat(fl);
  if (!HC || !AC || !FL || !BPD) return null;
  if ([BPD, HC, AC, FL].some(v => v <= 0 || v > 50)) return null; // rango plausible en cm
  const log10w = 1.3596 + 0.0064 * HC + 0.0424 * AC + 0.174 * FL + 0.00061 * BPD * AC - 0.00386 * AC * FL;
  return Math.round(10 ** log10w);
};

// Tabla de percentiles de peso fetal por semana (g) — Mikolajczyk R, Zhang
// J, Betran AP, Souza JP, Mori R, Gülmezoglu AM, Merialdi M. A global
// reference for fetal-weight and birthweight percentiles. Lancet 2011;
// 377(9780):1855-61 (adapta Hadlock FP et al., Radiology 1991;181(1):129-33).
const EFW_PERCENTILE_TABLE = {
  24: { 3: 537, 10: 582, 50: 678, 90: 802, 97: 820 },
  25: { 3: 627, 10: 680, 50: 792, 90: 936, 97: 957 },
  26: { 3: 727, 10: 788, 50: 918, 90: 1086, 97: 1110 },
  27: { 3: 837, 10: 907, 50: 1057, 90: 1250, 97: 1278 },
  28: { 3: 957, 10: 1037, 50: 1209, 90: 1429, 97: 1460 },
  29: { 3: 1086, 10: 1177, 50: 1372, 90: 1622, 97: 1658 },
  30: { 3: 1224, 10: 1327, 50: 1546, 90: 1828, 97: 1868 },
  31: { 3: 1370, 10: 1485, 50: 1730, 90: 2046, 97: 2091 },
  32: { 3: 1522, 10: 1650, 50: 1923, 90: 2273, 97: 2323 },
  33: { 3: 1679, 10: 1820, 50: 2121, 90: 2508, 97: 2563 },
  34: { 3: 1840, 10: 1994, 50: 2324, 90: 2748, 97: 2808 },
  35: { 3: 2002, 10: 2169, 50: 2528, 90: 2989, 97: 3055 },
  36: { 3: 2162, 10: 2343, 50: 2731, 90: 3229, 97: 3300 },
  37: { 3: 2319, 10: 2513, 50: 2929, 90: 3463, 97: 3540 },
  38: { 3: 2470, 10: 2677, 50: 3120, 90: 3688, 97: 3770 },
  39: { 3: 2612, 10: 2831, 50: 3299, 90: 3900, 97: 3986 },
  40: { 3: 2742, 10: 2972, 50: 3464, 90: 4095, 97: 4186 },
  41: { 3: 2859, 10: 3099, 50: 3611, 90: 4269, 97: 4364 },
};

// Interpolación lineal entre los anclajes de percentilo (3/10/50/90/97) de
// la semana más cercana — suficiente para una estimación clínica de uso
// general, no reemplaza el cálculo poblacional completo.
const estimateEfwPercentile = (efwGrams, gaWeeks) => {
  const week = Math.round(gaWeeks);
  const row = EFW_PERCENTILE_TABLE[Math.min(41, Math.max(24, week))];
  if (!row || !efwGrams) return null;
  const anchors = [3, 10, 50, 90, 97];
  if (efwGrams <= row[3]) return '<3';
  if (efwGrams >= row[97]) return '>97';
  for (let i = 0; i < anchors.length - 1; i++) {
    const [pLo, pHi] = [anchors[i], anchors[i + 1]];
    if (efwGrams >= row[pLo] && efwGrams <= row[pHi]) {
      const frac = (efwGrams - row[pLo]) / (row[pHi] - row[pLo]);
      return Math.round(pLo + frac * (pHi - pLo));
    }
  }
  return null;
};

const efwGrowthLabel = (percentile) => {
  if (percentile == null) return null;
  const p = typeof percentile === 'string' ? parseFloat(percentile.replace(/[<>]/g, '')) : percentile;
  if (percentile === '<3' || p < 3) return { label: 'Pequeño para EG (<p3)', color: 'error' };
  if (p < 10) return { label: 'Percentilo bajo (p3–p10)', color: 'warning' };
  if (p > 97 || percentile === '>97') return { label: 'Grande para EG (>p97)', color: 'error' };
  if (p > 90) return { label: 'Percentilo alto (p90–p97)', color: 'warning' };
  return { label: 'Adecuado para EG', color: 'success' };
};

// Checklist de factores de riesgo de preeclampsia — ACOG/NICE (no es un
// score numérico ni el algoritmo FMF): 1 factor de alto riesgo, o 2+ de
// riesgo moderado, indican considerar aspirina en dosis baja (12–28 sem,
// idealmente antes de sem 16). Ver ACOG Practice Advisory (2021, actualizado).
const PREECLAMPSIA_HIGH_RISK = [
  { key: 'prior_preeclampsia', label: 'Preeclampsia en embarazo previo' },
  { key: 'chronic_hypertension', label: 'Hipertensión crónica' },
  { key: 'diabetes', label: 'Diabetes tipo 1 o 2' },
  { key: 'renal_disease', label: 'Enfermedad renal crónica' },
  { key: 'autoimmune', label: 'Enfermedad autoinmune (LES, síndrome antifosfolípido)' },
  { key: 'multifetal', label: 'Embarazo múltiple' },
];
const PREECLAMPSIA_MODERATE_RISK = [
  { key: 'nulliparity', label: 'Nuliparidad' },
  { key: 'obesity', label: 'Obesidad (IMC ≥ 30)' },
  { key: 'family_history', label: 'Antecedente familiar de preeclampsia (madre o hermana)' },
  { key: 'age_over_35', label: 'Edad ≥ 35 años' },
  { key: 'ivf', label: 'Concepción por FIV' },
  { key: 'prior_low_birth_weight', label: 'Antecedente de bajo peso al nacer' },
  { key: 'interval_over_10y', label: 'Intervalo intergenésico > 10 años' },
];

const calcPreeclampsiaRisk = (selected) => {
  const highCount = PREECLAMPSIA_HIGH_RISK.filter(f => selected[f.key]).length;
  const moderateCount = PREECLAMPSIA_MODERATE_RISK.filter(f => selected[f.key]).length;
  const suggestAspirin = highCount >= 1 || moderateCount >= 2;
  return { highCount, moderateCount, suggestAspirin };
};

// ── VitalsChart ───────────────────────────────────────────────────────────

const VitalsChart = ({ records }) => {
  const data = [...records]
    .filter(r => r.vital_weight_kg || r.vital_blood_pressure || r.vital_heart_rate)
    .reverse()
    .map(r => ({
      fecha: fmtDate(r.created_at, { day: '2-digit', month: '2-digit' }),
      peso: r.vital_weight_kg ? parseFloat(r.vital_weight_kg) : null,
      fc: r.vital_heart_rate ? parseFloat(r.vital_heart_rate) : null,
      ta: r.vital_blood_pressure ? parseInt(r.vital_blood_pressure.split('/')[0]) : null,
    }));

  if (data.length < 2) return null;

  const hasPeso = data.some(d => d.peso != null);
  const hasFc = data.some(d => d.fc != null);
  const hasTa = data.some(d => d.ta != null);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
        <MonitorHeartIcon fontSize="small" color="primary" />
        <Typography variant="subtitle2" fontWeight={700}>Evolución de signos vitales</Typography>
        <Typography variant="caption" color="text.secondary">({data.length} registros con datos)</Typography>
      </Box>
      <ResponsiveContainer width="100%" height={150}>
        <LineChart data={data} margin={{ top: 4, right: 16, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="fecha" tick={{ fontSize: 10 }} />
          <YAxis tick={{ fontSize: 10 }} />
          <RechartsTooltip contentStyle={{ fontSize: 12 }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {hasPeso && <Line type="monotone" dataKey="peso" name="Peso (kg)" stroke="#1976d2" dot={{ r: 3 }} strokeWidth={2} connectNulls />}
          {hasTa && <Line type="monotone" dataKey="ta" name="TA sistólica" stroke="#e53935" dot={{ r: 3 }} strokeWidth={2} connectNulls />}
          {hasFc && <Line type="monotone" dataKey="fc" name="FC (lpm)" stroke="#e91e63" dot={{ r: 3 }} strokeWidth={2} strokeDasharray="4 2" connectNulls />}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

// Evolución del peso fetal estimado (EFW) control a control — feature
// nicho de obstetricia, solo se muestra si hay datos (el médico los carga
// desde la calculadora de biometría dentro del formulario de consulta).
const FetalGrowthChart = ({ records }) => {
  const data = [...records]
    .filter(r => r.vital_fetal_efw_g)
    .reverse()
    .map(r => ({
      fecha: fmtDate(r.created_at, { day: '2-digit', month: '2-digit' }),
      efw: parseFloat(r.vital_fetal_efw_g),
    }));

  if (data.length < 2) return null;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
        <MonitorHeartIcon fontSize="small" color="primary" />
        <Typography variant="subtitle2" fontWeight={700}>Evolución del peso fetal estimado</Typography>
        <Typography variant="caption" color="text.secondary">({data.length} controles)</Typography>
      </Box>
      <ResponsiveContainer width="100%" height={150}>
        <LineChart data={data} margin={{ top: 4, right: 16, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="fecha" tick={{ fontSize: 10 }} />
          <YAxis tick={{ fontSize: 10 }} />
          <RechartsTooltip contentStyle={{ fontSize: 12 }} formatter={(v) => [`${v} g`, 'Peso estimado']} />
          <Line type="monotone" dataKey="efw" name="Peso fetal estimado (g)" stroke="#7b1fa2" dot={{ r: 3 }} strokeWidth={2} connectNulls />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

// ── SignatureDetail (texto "Firmada" clickeable con datos técnicos) ───────
// El usuario pidió que al clickear la firma se vean datos técnicos "para
// darle seriedad" — un sello sin nada detrás no transmite que hay una
// verificación criptográfica real. Muestra algoritmo, fingerprint (hash
// corto del JWT, no el token completo — misma decisión de no exponer el
// JWT crudo que ya existía) y el id del registro firmado. Pedido explícito
// del usuario 2026-10-30.
const SignatureDetail = ({ record }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  if (!record.is_signed) return null;
  return (
    <>
      <Box
        component="span"
        onClick={(e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); }}
        sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
      >
        {' · '}<VerifiedIcon sx={{ fontSize: '0.8rem', verticalAlign: 'text-bottom', color: 'success.main', mx: 0.25 }} />
        Firmada{record.signed_at ? ` el ${fmtDateTime(record.signed_at)}` : ''}
      </Box>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 1.75, maxWidth: 300 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
            <VerifiedIcon sx={{ fontSize: '1rem', color: 'success.main' }} />
            <Typography variant="subtitle2" fontWeight={700}>Firma electrónica</Typography>
          </Box>
          <Stack spacing={0.5}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
              <Typography variant="caption" color="text.secondary">Firmado por</Typography>
              <Typography variant="caption" fontWeight={600} sx={{ textAlign: 'right' }}>
                {record.doctor_name || 'médico no identificado'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
              <Typography variant="caption" color="text.secondary">Fecha de firma</Typography>
              <Typography variant="caption" fontWeight={600}>
                {record.signed_at ? fmtDateTime(record.signed_at) : 'No disponible'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
              <Typography variant="caption" color="text.secondary">Algoritmo</Typography>
              <Typography variant="caption" fontWeight={600} fontFamily="monospace">
                {record.signature_algorithm || '—'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
              <Typography variant="caption" color="text.secondary">Huella (SHA-256)</Typography>
              <Typography variant="caption" fontWeight={600} fontFamily="monospace">
                {record.signature_fingerprint || '—'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
              <Typography variant="caption" color="text.secondary">Registro</Typography>
              <Typography variant="caption" fontWeight={600} fontFamily="monospace">#{record.id}</Typography>
            </Box>
          </Stack>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1.25 }}>
            Firma criptográfica RSA (RS256) generada automáticamente al finalizar la consulta.
          </Typography>
        </Box>
      </Popover>
    </>
  );
};

// ── RecordCardCompact (para Resumen) ──────────────────────────────────────

const RecordCardCompact = ({ record, onClick }) => (
  <Paper
    variant="outlined"
    onClick={onClick}
    sx={{
      p: 1.75, cursor: 'pointer', borderRadius: 2,
      '&:hover': { bgcolor: 'primary.50', borderColor: 'primary.300' },
      transition: 'all 0.15s',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>
        {fmtDate(record.created_at)}
      </Typography>
      {record.consultation_type && (
        <Chip label={record.consultation_type} size="small" color="primary" variant="outlined"
          sx={{ height: 18, fontSize: '0.6rem' }} />
      )}
      {/* Autoguardado cada 30s y "Guardar" (que finaliza la consulta) eran
          dos cosas distintas sin ninguna señal visible de que se mezclaban —
          un médico que cerraba confiando en el autosave podía dejar una
          consulta en borrador sin darse cuenta. Pedido explícito del
          usuario: avisar los borradores sin finalizar en vez de forzar un
          único modelo de guardado. */}
      {record.status !== 'finalizado' && (
        <Chip label="Borrador" size="small" color="warning"
          sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700 }} />
      )}
    </Box>
    {(record.notes || record.chief_complaint) && (
      <Typography variant="body2" sx={{
        mb: 0.25, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
        overflow: 'hidden', lineHeight: 1.35, whiteSpace: 'pre-line',
      }}>
        {recordSummary(record.notes || record.chief_complaint)}
      </Typography>
    )}
  </Paper>
);

// ── StudyRow (reutilizable en Resumen y Estudios) ─────────────────────────

const StudyRow = ({ study, onDelete, onPreview, onRename, compact = false }) => {
  const cat = STUDY_CATEGORIES[study.category] || STUDY_CATEGORIES.otro;
  const notify = useNotification();
  // Mismo criterio que combinedFiles en RecordFullDetail: solo imágenes y
  // PDF se pueden previsualizar inline, el resto (Word, Excel, etc.) se
  // sigue descargando directo.
  const isViewable = isPreviewable(study.mime_type);

  const handleDownload = async () => {
    try {
      const res = await downloadPatientStudy(study.id);
      downloadBlobAsFile(res.data, study.original_filename, study.mime_type);
    } catch (err) {
      notify(err.userMessage || 'No se pudo descargar el archivo', 'error');
    }
  };

  // Mismo patrón de rename que RecordFullDetail (archivos dentro de una
  // consulta) — antes solo existía ahí, un estudio cargado desde la
  // pestaña "Estudios" standalone no se podía renombrar. Pedido explícito
  // del usuario: dejar cambiar el nombre en ambos casos.
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  // onRename solo se pasa desde EstudiosTab (canManage) — ResumenTab usa
  // StudyRow en modo compact/solo-lectura y no lo pasa, mismo gate que onDelete.
  const canRename = !!onRename;

  const startRename = (e) => {
    e.stopPropagation();
    setRenameValue(study.description || study.original_filename);
    setIsRenaming(true);
  };
  const cancelRename = () => setIsRenaming(false);
  const saveRename = async () => {
    const newName = renameValue.trim();
    setIsRenaming(false);
    if (!newName || newName === (study.description || study.original_filename)) return;
    try {
      await updatePatientStudy(study.id, { description: newName });
      onRename(study.id, newName);
    } catch (err) {
      notify(err.userMessage || 'No se pudo renombrar', 'error');
    }
  };

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.5,
      p: compact ? 1 : 1.5, borderRadius: 1.5,
      border: '1px solid', borderColor: 'divider',
      cursor: isViewable && onPreview ? 'pointer' : 'default',
      '&:hover': { bgcolor: 'grey.50' },
    }}
    onClick={() => isViewable && onPreview?.(study)}
    >
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 32, height: 32, borderRadius: 1,
        bgcolor: cat.bg, color: cat.color, flexShrink: 0,
      }}>
        {cat.icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Chip label={cat.label} size="small"
            sx={{ height: 18, fontSize: '0.6rem', bgcolor: cat.bg, color: cat.color, fontWeight: 700 }} />
          {study.study_date && (
            <Typography variant="caption" color="text.secondary">{fmtDate(study.study_date)}</Typography>
          )}
        </Box>
        {isRenaming ? (
          <TextField
            size="small" variant="standard" autoFocus fullWidth
            value={renameValue} onChange={(e) => setRenameValue(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => { if (e.key === 'Enter') saveRename(); if (e.key === 'Escape') cancelRename(); }}
            onBlur={saveRename}
          />
        ) : (
          // noWrap de MUI solo pone white-space:nowrap — sin overflow+
          // textOverflow explícitos el texto largo se desbordaba por encima
          // de los botones de descargar/eliminar en vez de truncarse.
          // Siempre se muestra algo clickeable (descripción o el nombre de
          // archivo) para poder renombrar, igual con o sin descripción ya
          // cargada — antes, sin descripción, no había nada para click acá.
          <Typography variant="body2" fontWeight={600} noWrap
            onClick={(e) => { if (canRename) startRename(e); }}
            sx={{
              overflow: 'hidden', textOverflow: 'ellipsis',
              cursor: canRename ? 'text' : 'default',
              '&:hover': canRename ? { textDecoration: 'underline' } : {},
            }}>
            {study.description || study.original_filename}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {study.original_filename}{study.file_size_bytes ? ` · ${formatBytes(study.file_size_bytes)}` : ''}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
        <Tooltip title="Descargar">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDownload(); }}><DownloadIcon fontSize="small" /></IconButton>
        </Tooltip>
        {onDelete && (
          <Tooltip title="Eliminar">
            <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); onDelete(study); }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

// ── StudyGroupCard (varios archivos de un mismo estudio, ej. ecografía con
// imágenes + informe) ──────────────────────────────────────────────────────
// Reemplaza a StudyRow cuando un "estudio" tiene más de un archivo. Pedido
// explícito del usuario 2026-10-28: un médico reportó que a veces recibe 3
// archivos de un mismo estudio (imágenes + informe) y quiere verlos juntos,
// no como filas sueltas sin relación entre sí.

const StudyGroupCard = ({ group, onDelete, onPreview, onRename, onAddFile }) => {
  const cat = STUDY_CATEGORIES[group.category] || STUDY_CATEGORIES.otro;
  const notify = useNotification();
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [addingFile, setAddingFile] = useState(false);
  const canRename = !!onRename;
  const first = group.files[0];

  const handleDownload = async (study) => {
    try {
      const res = await downloadPatientStudy(study.id);
      downloadBlobAsFile(res.data, study.original_filename, study.mime_type);
    } catch (err) {
      notify(err.userMessage || 'No se pudo descargar el archivo', 'error');
    }
  };

  const startRename = (e) => {
    e.stopPropagation();
    setRenameValue(group.description || '');
    setIsRenaming(true);
  };
  const cancelRename = () => setIsRenaming(false);
  const saveRename = async () => {
    const newName = renameValue.trim();
    setIsRenaming(false);
    if (!newName || newName === (group.description || '')) return;
    try {
      await updatePatientStudy(first.id, { description: newName });
      onRename(group.group_id, newName);
    } catch (err) {
      notify(err.userMessage || 'No se pudo renombrar', 'error');
    }
  };

  const handleAddFileClick = () => {
    if (addingFile) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.onchange = async () => {
      if (input.files?.[0]) {
        setAddingFile(true);
        try { await onAddFile(group, input.files[0]); }
        finally { setAddingFile(false); }
      }
    };
    input.click();
  };

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', gap: 1,
      p: 1.5, borderRadius: 1.5,
      border: '1px solid', borderColor: 'divider',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 32, height: 32, borderRadius: 1,
          bgcolor: cat.bg, color: cat.color, flexShrink: 0,
        }}>
          {cat.icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            <Chip label={cat.label} size="small"
              sx={{ height: 18, fontSize: '0.6rem', bgcolor: cat.bg, color: cat.color, fontWeight: 700 }} />
            <Chip label={`${group.files.length} archivos`} size="small" variant="outlined"
              sx={{ height: 18, fontSize: '0.6rem' }} />
            {group.study_date && (
              <Typography variant="caption" color="text.secondary">{fmtDate(group.study_date)}</Typography>
            )}
          </Box>
          {isRenaming ? (
            <TextField
              size="small" variant="standard" autoFocus fullWidth
              value={renameValue} onChange={(e) => setRenameValue(e.target.value)}
              placeholder="Descripción del estudio"
              onKeyDown={(e) => { if (e.key === 'Enter') saveRename(); if (e.key === 'Escape') cancelRename(); }}
              onBlur={saveRename}
            />
          ) : (
            <Typography variant="body2" fontWeight={600} noWrap
              onClick={canRename ? startRename : undefined}
              sx={{
                overflow: 'hidden', textOverflow: 'ellipsis',
                cursor: canRename ? 'text' : 'default',
                '&:hover': canRename ? { textDecoration: 'underline' } : {},
              }}>
              {group.description || 'Estudio sin descripción'}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', pl: '44px' }}>
        {group.files.map((f) => {
          const isViewable = isPreviewable(f.mime_type);
          return (
            <Chip
              key={f.id}
              icon={FILE_ICON(f.mime_type)}
              label={f.original_filename}
              size="small"
              variant="outlined"
              onClick={() => isViewable ? onPreview?.(f) : handleDownload(f)}
              onDelete={onDelete ? () => onDelete(f) : undefined}
              sx={{ maxWidth: 220 }}
            />
          );
        })}
        {onAddFile && (
          <Chip
            icon={addingFile ? <CircularProgress size={12} /> : <AddIcon sx={{ fontSize: 14 }} />}
            label="Agregar archivo"
            size="small"
            variant="outlined"
            onClick={handleAddFileClick}
            disabled={addingFile}
            sx={{ borderStyle: 'dashed' }}
          />
        )}
      </Box>
    </Box>
  );
};

// ── RecordFullDetail (dentro del acordeón de Consultas) ───────────────────

const RecordFullDetail = ({ record, onSaved, patientId }) => {
  const { isAdmin, isMedico } = useAuth();
  const notify = useNotification();
  const { features: clinicFeatures } = useClinicFeatures();
  const obstetricsEnabled = !!clinicFeatures?.obstetrics;
  const [files, setFiles] = useState([]);
  const [studies, setStudies] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState('');
  const [viewerIndex, setViewerIndex] = useState(null);
  const [studyDialogOpen, setStudyDialogOpen] = useState(false);
  const [studyPendingFile, setStudyPendingFile] = useState(null);
  const [studyCategory, setStudyCategory] = useState('otro');
  const [studyDescription, setStudyDescription] = useState('');
  const canManage = isAdmin || isMedico;
  // Se combinan archivos de la nota + estudios complementarios subidos
  // desde esta misma consulta en una sola lista visual (con un chip que
  // distingue cada tipo) — antes solo había archivos de nota, sin forma de
  // marcar algo como "esto es un estudio" para que apareciera también en
  // la pestaña Estudios del paciente.
  const combinedFiles = [
    ...files.map(f => ({ ...f, _kind: 'file' })),
    ...studies.map(s => ({ ...s, _kind: 'study' })),
  ].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  // Archivos que se pueden previsualizar inline en el visor (RecordFileViewer)
  // en vez de solo descargarse — antes solo imágenes; PDFs (el caso más común
  // de informes/estudios adjuntos) quedaban sin preview, pedido explícito.
  const viewableFiles = combinedFiles.filter(f => isPreviewable(f.mime_type));

  // Ventana de edición de 24hs desde que se finalizó — mismo criterio que
  // el backend (routers/history.py). Sin finalized_at (todavía borrador,
  // caso raro de verse acá porque el listado por default excluye
  // borradores) no hay límite de tiempo.
  const editableWithin24h = !record.finalized_at
    || (Date.now() - _asUtcDate(record.finalized_at).getTime()) < 24 * 60 * 60 * 1000;
  const [isEditing, setIsEditing] = useState(false);
  const [editNotes, setEditNotes] = useState(record.notes || '');
  const [savingEdit, setSavingEdit] = useState(false);

  const handleSaveEdit = async () => {
    setSavingEdit(true);
    try {
      // PUT /records/{id} pisa TODOS los campos del schema con lo que se
      // manda acá (no hace merge parcial) — listar los campos a mano
      // quedaba desactualizado cada vez que se agregaba uno nuevo (pasó
      // con la biometría fetal y el checklist de preeclampsia, que se
      // borraban silenciosamente al editar solo las notas). Se arma el
      // payload excluyendo del `record` (viene del GET) los campos que no
      // pertenecen al schema de entrada MedicalRecordCreate, y se pisa
      // solo `notes`, que es lo único editable en este formulario.
      const RESPONSE_ONLY_FIELDS = [
        'id', 'patient_id', 'doctor_name', 'created_by_username', 'clinic_id',
        'created_by_user_id', 'created_at', 'finalized_at',
        'chief_complaint', 'examination_findings', 'diagnosis', 'treatment',
      ];
      const editablePayload = Object.fromEntries(
        Object.entries(record).filter(([key]) => !RESPONSE_ONLY_FIELDS.includes(key))
      );
      await updateMedicalRecord(patientId, record.id, { ...editablePayload, notes: sanitizeNotesHtml(editNotes) });
      setIsEditing(false);
      notify('Consulta actualizada', 'success');
      onSaved?.();
    } catch (err) {
      notify(err.userMessage || 'No se pudo guardar la edición', 'error');
    } finally {
      setSavingEdit(false);
    }
  };

  const loadFiles = useCallback(async () => {
    try {
      const [filesRes, studiesRes] = await Promise.all([getRecordFiles(record.id), getRecordStudies(record.id)]);
      setFiles(filesRes.data || []);
      setStudies(studiesRes.data || []);
    } catch { }
  }, [record.id]);

  useEffect(() => { loadFiles(); }, [loadFiles]);

  const handleUpload = async (fileOrFiles) => {
    const filesToUpload = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles];
    if (filesToUpload.length === 0) return;
    setUploading(true); setUploadErr('');
    const failed = [];
    for (const file of filesToUpload) {
      try { await uploadRecordFile(record.id, file); }
      catch (err) { failed.push(file.name); }
    }
    await loadFiles();
    if (failed.length > 0) setUploadErr(`No se pudieron subir: ${failed.join(', ')}`);
    setUploading(false);
  };

  const openStudyDialog = (file) => { setStudyPendingFile(file); setStudyCategory('otro'); setStudyDescription(''); setStudyDialogOpen(true); };
  const closeStudyDialog = () => { setStudyDialogOpen(false); setStudyPendingFile(null); };

  const handleUploadStudy = async () => {
    if (!studyPendingFile) return;
    setUploading(true); setUploadErr('');
    try {
      await uploadRecordStudy(record.id, studyPendingFile, { category: studyCategory, description: studyDescription });
      closeStudyDialog();
      await loadFiles();
    } catch (err) { setUploadErr(err.userMessage || 'Error al subir el estudio'); }
    finally { setUploading(false); }
  };

  const handleDownload = async (f) => {
    try {
      const res = f._kind === 'study' ? await downloadPatientStudy(f.id) : await downloadRecordFile(f.id);
      downloadBlobAsFile(res.data, f.original_filename, f.mime_type);
    } catch (err) { notify(err.userMessage || 'Error al descargar el archivo', 'error'); }
  };

  const deleteConfirm = useConfirmDialog();

  const confirmDelete = async (f) => {
    deleteConfirm.close();
    try {
      if (f._kind === 'study') {
        await deletePatientStudy(f.id);
        setStudies(prev => prev.filter(x => x.id !== f.id));
      } else {
        await deleteRecordFile(f.id);
        setFiles(prev => prev.filter(x => x.id !== f.id));
      }
    } catch (err) { notify(err.userMessage || 'Error al eliminar el archivo', 'error'); }
  };

  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const startRename = (f) => { setRenamingId(f.id); setRenameValue(f.display_name || f.original_filename); };
  const cancelRename = () => { setRenamingId(null); setRenameValue(''); };

  const saveRename = async (f) => {
    const newName = renameValue.trim();
    cancelRename();
    if (!newName || newName === (f.display_name || f.original_filename)) return;
    try {
      if (f._kind === 'study') {
        await updatePatientStudy(f.id, { description: newName });
        setStudies(prev => prev.map(x => x.id === f.id ? { ...x, description: newName } : x));
      } else {
        await renameRecordFile(f.id, newName);
        setFiles(prev => prev.map(x => x.id === f.id ? { ...x, display_name: newName } : x));
      }
    } catch (err) { notify(err.userMessage || 'No se pudo renombrar', 'error'); }
  };

  const vitals = Object.entries(VITAL_LABELS).filter(([k]) => record[k]).map(([k, label]) => ({ label, value: record[k] }));
  const recordBmi = calcBMI(record.vital_weight_kg, record.vital_height_cm);

  const Field = ({ label, value }) => value ? (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={700}
        sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.25 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{value}</Typography>
    </Box>
  ) : null;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          {record.doctor_name ? `Dr/a. ${record.doctor_name}` : 'Médico no especificado'}
          {/* Matrícula junto al nombre — mismo orden (MN, MP) y formato que
              pidió el usuario explícitamente 2026-10-30, para dar más
              formalidad a la firma. */}
          {[
            record.doctor_matricula_nacional && `MN ${record.doctor_matricula_nacional}`,
            record.doctor_matricula_provincial && `MP ${record.doctor_matricula_provincial}`,
          ].filter(Boolean).map((m) => ` ${m}`).join('')}
          {record.created_by_username && record.created_by_username !== record.doctor_name ? ` (cargado por ${record.created_by_username})` : ''}
          {' · '}{fmtDateTime(record.created_at)}
          {/* Firma electrónica: todas las evoluciones finalizadas la tienen,
              así que un chip aparte no sumaba información (pedido explícito
              del usuario 2026-10-30) — queda integrada en esta misma línea,
              clickeable para ver datos técnicos (algoritmo, huella, registro)
              y darle seriedad de verificación real. */}
          <SignatureDetail record={record} />
        </Typography>
        {canManage && !isEditing && record.status === 'finalizado' && (
          editableWithin24h ? (
            <Button size="small" startIcon={<EditIcon sx={{ fontSize: 14 }} />}
              onClick={() => { setEditNotes(record.notes || ''); setIsEditing(true); }}
              sx={{ fontSize: '0.7rem', flexShrink: 0 }}>
              Editar
            </Button>
          ) : (
            <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>
              Ya no editable (pasaron 24hs)
            </Typography>
          )
        )}
      </Box>

      {isEditing ? (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Evolución
          </Typography>
          <RichTextEditor
            value={editNotes} onChange={setEditNotes} autoFocus minRows={8}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
            <Button size="small" onClick={() => setIsEditing(false)} disabled={savingEdit}>Cancelar</Button>
            <Button size="small" variant="contained" onClick={handleSaveEdit} disabled={savingEdit}>
              {savingEdit ? <CircularProgress size={16} /> : 'Guardar cambios'}
            </Button>
          </Box>
        </Box>
      ) : (
        <>
          {(record.notes || record.chief_complaint) && (
            <Box sx={{ mb: 1.5 }}>
              {/* Antes era una caption chica casi al mismo peso que el
                  cuerpo de abajo — ahora un borde inferior la separa
                  claramente como encabezado de sección, en vez de
                  mezclarse visualmente con las primeras líneas del
                  texto libre que el médico tipeó. */}
              <Typography variant="caption" color="text.secondary" fontWeight={700}
                sx={{
                  textTransform: 'uppercase', letterSpacing: 0.5, display: 'block',
                  pb: 0.5, mb: 1, borderBottom: '1px solid', borderColor: 'divider',
                }}>
                Evolución
              </Typography>
              <RichTextView html={record.notes || record.chief_complaint} />
            </Box>
          )}
          {/* Legacy: registros de antes de la migración a campo único que no
              llegaron a fusionarse en `notes` por algún motivo — se siguen
              mostrando para no perder información ya cargada. */}
          <Field label="Examen físico / Hallazgos" value={record.examination_findings} />
          <Field label="Diagnóstico" value={record.notes ? null : record.diagnosis} />
          <Field label="Tratamiento / Indicaciones" value={record.treatment} />
        </>
      )}

      {(vitals.length > 0 || recordBmi) && (
        <Box sx={{ mt: 1, mb: 2 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700}
            sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
            Signos vitales
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {vitals.map(v => (
              <Chip key={v.label} label={`${v.label}: ${v.value}`} size="small" variant="outlined" />
            ))}
            {recordBmi && (
              <Chip label={`IMC: ${recordBmi.value} — ${recordBmi.label}`} size="small"
                color={recordBmi.label === 'Normal' ? 'success' : recordBmi.label === 'Bajo peso' ? 'warning' : 'error'} />
            )}
          </Box>
        </Box>
      )}

      {/* Obstetricia — feature nicho, solo se muestra con el flag activo y
          si esta consulta tiene datos cargados. */}
      {obstetricsEnabled && (record.vital_fetal_efw_g || record.preeclampsia_risk_factors || record.edd_date) && (
        <Box sx={{ mt: 1, mb: 2 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700}
            sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
            Obstetricia
          </Typography>
          {record.edd_date && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 0.75 }}>
              <Chip
                label={`FPP: ${record.edd_date}${record.gest_age_weeks ? ` (${record.gest_age_weeks} sem${record.gest_age_days ? ` y ${record.gest_age_days} días` : ''} al momento de esta consulta)` : ''}`}
                size="small" color="secondary" variant="outlined"
              />
            </Box>
          )}
          {record.vital_fetal_efw_g && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: record.preeclampsia_risk_factors ? 0.75 : 0 }}>
              <Chip label={`Peso estimado: ${Number(record.vital_fetal_efw_g).toLocaleString('es-AR')} g`} size="small" color="primary" variant="outlined" />
              {record.vital_fetal_bpd_mm && <Chip label={`DBP: ${record.vital_fetal_bpd_mm} mm`} size="small" variant="outlined" />}
              {record.vital_fetal_hc_mm && <Chip label={`CC: ${record.vital_fetal_hc_mm} mm`} size="small" variant="outlined" />}
              {record.vital_fetal_ac_mm && <Chip label={`CA: ${record.vital_fetal_ac_mm} mm`} size="small" variant="outlined" />}
              {record.vital_fetal_fl_mm && <Chip label={`LF: ${record.vital_fetal_fl_mm} mm`} size="small" variant="outlined" />}
            </Box>
          )}
          {record.preeclampsia_risk_factors && (() => {
            let keys = [];
            try { keys = JSON.parse(record.preeclampsia_risk_factors); } catch { keys = []; }
            const allFactors = [...PREECLAMPSIA_HIGH_RISK, ...PREECLAMPSIA_MODERATE_RISK];
            const labels = keys.map(k => allFactors.find(f => f.key === k)?.label).filter(Boolean);
            if (labels.length === 0) return null;
            return (
              <Typography variant="caption" color="text.secondary">
                Factores de riesgo de preeclampsia: {labels.join(', ')}
              </Typography>
            );
          })()}
        </Box>
      )}

      {/* Archivos del registro */}
      <Divider sx={{ my: 1.5 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={700}
          sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Archivos {combinedFiles.length > 0 && `(${combinedFiles.length})`}
        </Typography>
        {canManage && (
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            <AttachFileButton onFilesSelected={handleUpload} onFilesRejected={setUploadErr} multiple loading={uploading} label="Adjuntar" />
            <AttachFileButton onFilesSelected={openStudyDialog} onFilesRejected={setUploadErr} loading={uploading} label="Adjuntar estudio" />
          </Box>
        )}
      </Box>
      {uploadErr && <Alert severity="error" sx={{ mb: 1 }}>{uploadErr}</Alert>}
      {uploading && <LinearProgress sx={{ mb: 1 }} />}
      {combinedFiles.length === 0 ? (
        <Typography variant="caption" color="text.secondary">Sin archivos en esta consulta.</Typography>
      ) : (
        <Stack spacing={0.5}>
          {combinedFiles.map(f => {
            const isViewable = isPreviewable(f.mime_type);
            const displayLabel = f.display_name || (f._kind === 'study' ? f.description : null) || f.original_filename;
            const isRenaming = renamingId === f.id;
            return (
              <Box key={`${f._kind}-${f.id}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 0.75,
                border: '1px solid', borderColor: 'divider', borderRadius: 1,
                cursor: isViewable && !isRenaming ? 'pointer' : 'default',
                '&:hover': isViewable && !isRenaming ? { bgcolor: 'action.hover' } : {} }}
                onClick={() => isViewable && !isRenaming && setViewerIndex(viewableFiles.findIndex(x => x._kind === f._kind && x.id === f.id))}
              >
                <Box sx={{ color: 'text.secondary', display: 'flex' }}>{FILE_ICON(f.mime_type)}</Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {isRenaming ? (
                    <TextField
                      size="small" variant="standard" autoFocus fullWidth
                      value={renameValue} onChange={(e) => setRenameValue(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => { if (e.key === 'Enter') saveRename(f); if (e.key === 'Escape') cancelRename(); }}
                      onBlur={() => saveRename(f)}
                    />
                  ) : (
                    <Typography variant="caption" noWrap
                      onClick={(e) => { if (canManage) { e.stopPropagation(); startRename(f); } }}
                      sx={{ cursor: canManage ? 'text' : 'default', '&:hover': canManage ? { textDecoration: 'underline' } : {} }}>
                      {displayLabel}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {formatBytes(f.file_size_bytes)} · {fmtDate(f.created_at)}
                    {f._kind === 'study' && (
                      <Chip label={STUDY_CATEGORIES[f.category]?.label || 'Estudio'} size="small"
                        sx={{ height: 16, fontSize: '0.6rem', ml: 0.5 }} />
                    )}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDownload(f); }}><DownloadIcon fontSize="small" /></IconButton>
                {canManage && (
                  <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); deleteConfirm.open(f); }}><DeleteIcon fontSize="small" /></IconButton>
                )}
              </Box>
            );
          })}
        </Stack>
      )}
      {viewerIndex !== null && (
        <RecordFileViewer
          files={viewableFiles}
          index={viewerIndex}
          onIndexChange={setViewerIndex}
          onClose={() => setViewerIndex(null)}
          onDownload={handleDownload}
        />
      )}

      {/* Diálogo de categoría al marcar un archivo como estudio complementario */}
      <Dialog open={studyDialogOpen} onClose={closeStudyDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Adjuntar como estudio complementario</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <Typography variant="caption" color="text.secondary">
            {studyPendingFile?.name}
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Categoría</InputLabel>
            <Select value={studyCategory} label="Categoría" onChange={e => setStudyCategory(e.target.value)}>
              {Object.entries(STUDY_CATEGORIES).map(([k, c]) => (
                <MenuItem key={k} value={k}>{c.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField fullWidth size="small" label="Descripción (opcional, ej: Ecografía 20 sem)"
            value={studyDescription} onChange={e => setStudyDescription(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeStudyDialog}>Cancelar</Button>
          <Button variant="contained" onClick={handleUploadStudy} disabled={uploading}>
            {uploading ? <CircularProgress size={16} /> : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteConfirm.pending}
        title="Eliminar archivo"
        message={`Eliminar "${deleteConfirm.pending?.display_name || deleteConfirm.pending?.original_filename}"?`}
        onCancel={deleteConfirm.close}
        onConfirm={() => confirmDelete(deleteConfirm.pending)}
      />
    </Box>
  );
};

// Visor tipo carrousel para las imágenes de una consulta — mismo patrón de
// blob autenticado que DecisionMedia.js (los archivos de HC requieren JWT,
// no se pueden referenciar con <img src> directo). Antes cada imagen solo
// se podía descargar una por una, sin forma de recorrer todas las de la
// consulta ni verlas en grande.
const RecordFileViewer = ({ files, index, onIndexChange, onClose, onDownload }) => {
  const [url, setUrl] = useState(null);
  const [loadErr, setLoadErr] = useState(false);
  const current = files[index];

  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;
    setUrl(null); setLoadErr(false);
    if (!current) return;
    (current._kind === 'study' ? downloadPatientStudy(current.id) : downloadRecordFile(current.id))
      .then((res) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setUrl(objectUrl);
      })
      .catch(() => { if (!cancelled) setLoadErr(true); });
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [current?.id]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && index > 0) onIndexChange(index - 1);
      else if (e.key === 'ArrowRight' && index < files.length - 1) onIndexChange(index + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, files.length, onClose, onIndexChange]);

  if (!current) return null;

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { bgcolor: 'black', boxShadow: 'none' } }}>
      <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column',
        minHeight: '60vh', maxHeight: '90vh' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          p: 1, color: 'white' }}>
          <Typography variant="caption" noWrap sx={{ ml: 1 }}>
            {current.display_name || current.description || current.original_filename} · {index + 1} / {files.length}
          </Typography>
          <Box>
            <IconButton size="small" sx={{ color: 'white' }} onClick={() => onDownload(current)}>
              <DownloadIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" sx={{ color: 'white' }} onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ position: 'relative', flex: 1, display: 'flex', alignItems: 'center',
          justifyContent: 'center', overflow: 'hidden' }}>
          {index > 0 && (
            <IconButton onClick={() => onIndexChange(index - 1)}
              sx={{ position: 'absolute', left: 8, color: 'white', bgcolor: 'rgba(0,0,0,0.4)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' } }}>
              <ChevronLeftIcon />
            </IconButton>
          )}

          {loadErr ? (
            <Typography variant="body2" sx={{ color: 'white' }}>No se pudo cargar el archivo</Typography>
          ) : !url ? (
            <CircularProgress sx={{ color: 'white' }} />
          ) : current.mime_type === 'application/pdf' ? (
            // El navegador renderiza el PDF con su visor nativo dentro del
            // iframe (zoom, scroll, búsqueda) — no hace falta ninguna
            // librería extra para esto.
            <Box component="iframe" src={url} title={current.original_filename}
              sx={{ width: '100%', height: '80vh', border: 'none', bgcolor: 'white' }} />
          ) : (
            <Box component="img" src={url} alt={current.original_filename}
              sx={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }} />
          )}

          {index < files.length - 1 && (
            <IconButton onClick={() => onIndexChange(index + 1)}
              sx={{ position: 'absolute', right: 8, color: 'white', bgcolor: 'rgba(0,0,0,0.4)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' } }}>
              <ChevronRightIcon />
            </IconButton>
          )}
        </Box>

        {files.length > 1 && (
          <Box sx={{ display: 'flex', gap: 0.5, p: 1, overflowX: 'auto' }}>
            {files.map((f, i) => (
              <Box key={f.id} onClick={() => onIndexChange(i)}
                sx={{ width: 44, height: 44, flexShrink: 0, borderRadius: 1, cursor: 'pointer',
                  border: '2px solid', borderColor: i === index ? 'primary.main' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: 'rgba(255,255,255,0.1)', color: 'white' }}>
                {f.mime_type === 'application/pdf' ? <PictureAsPdfIcon fontSize="small" /> : <ImageIcon fontSize="small" />}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

// ── NewRecordForm ─────────────────────────────────────────────────────────

export const NewRecordForm = ({ patient, records: patientRecords = [], onSaved, onCancel, doctors: doctorsProp, problems: problemsProp = [], initialProblemId = '', appointmentId = null, initialDoctorId = null, appointmentNote = null }) => {
  const { user, doctorId, isMedico } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [form, setForm] = useState(emptyRecord);
  const [selectedDoctorId, setSelectedDoctorId] = useState(doctorId || initialDoctorId || '');
  const [selectedProblemId, setSelectedProblemId] = useState(initialProblemId || '');
  const [doctors, setDoctors] = useState(doctorsProp || []);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  // Antes CONSULTATION_TYPES era una lista fija de 5 strings hardcodeados
  // sin forma de agregar uno nuevo — pedido explícito del usuario
  // (2026-10-12): editable, con buscador y creación al vuelo. Mismo
  // patrón que "+ Crear tipo de turno nuevo" en AppointmentDialog.js, pero
  // con Autocomplete (no Select) porque el pedido fue específicamente
  // "agregar un buscador dentro del desplegable".
  const [consultationTypes, setConsultationTypes] = useState([]);
  const [creatingConsultationType, setCreatingConsultationType] = useState(false);
  // Si ya hay un embarazo en curso (FPP guardada en una consulta anterior,
  // todavía no vencida), se precarga acá directamente en el estado inicial
  // —lazy initializer, corre una sola vez— así el médico ve la edad
  // gestacional de hoy ya calculada sin tener que volver a cargar el dato
  // ancla. ongoingPregnancy queda afuera del useState para poder mostrar
  // el aviso y el botón de "cargar datos nuevos" más abajo.
  const ongoingPregnancy = useMemo(() => findOngoingPregnancy(patientRecords), [patientRecords]);
  const [egaMethod, setEgaMethod] = useState(() => ongoingPregnancy ? 'edd' : 'fum');
  const [egaInputs, setEgaInputs] = useState(() => ({
    fum: '', sonoDate: '', sonoWeeks: '', sonoDays: '0', crl: '',
    edd: ongoingPregnancy?.edd || '',
  }));
  // true mientras el valor de la calculadora sigue siendo el auto-cargado
  // sin que el médico lo haya tocado — controla si se muestra el aviso.
  const [usingOngoingPregnancy, setUsingOngoingPregnancy] = useState(!!ongoingPregnancy);
  const [preeclampsiaFactors, setPreeclampsiaFactors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [error, setError] = useState('');
  const [pendingFiles, setPendingFiles] = useState([]);
  const [draftId, setDraftId] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  useEffect(() => { if (isMedico && doctorId) setSelectedDoctorId(doctorId); }, [isMedico, doctorId]);
  useEffect(() => { if (doctorsProp) setDoctors(doctorsProp); }, [doctorsProp]);
  useEffect(() => {
    if (!doctorsProp) {
      getDoctors({ limit: 500 }).then(r => setDoctors(r.data?.items || [])).catch(() => {});
    }
    getConsultationTemplates().then(r => setTemplates(r.data || [])).catch(() => {});
    getConsultationTypes().then(r => setConsultationTypes(r.data || [])).catch(() => {});
  }, []);

  const set = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
    setIsDirty(true);
  };

  // Autocomplete freeSolo: si el médico elige un valor existente, solo
  // guarda el nombre en form.consultation_type (sigue siendo String libre
  // en MedicalRecord, no FK — ver modelo ConsultationType). Si escribe uno
  // que no está en la lista y confirma, se crea en el catálogo de la
  // clínica al vuelo (mismo criterio que appointment types) para que
  // aparezca sugerido la próxima vez, sin bloquear el guardado de la
  // consulta si la creación del tipo falla por algún motivo.
  const handleConsultationTypeChange = async (newValue) => {
    const name = (newValue || '').trim();
    setForm(prev => ({ ...prev, consultation_type: name }));
    setIsDirty(true);
    if (!name) return;
    const exists = consultationTypes.some(t => t.name.toLowerCase() === name.toLowerCase());
    if (exists) return;
    setCreatingConsultationType(true);
    try {
      const res = await createConsultationType({ name });
      setConsultationTypes(prev => [...prev, res.data]);
    } catch { /* la consulta se puede guardar igual con el texto libre */ }
    finally { setCreatingConsultationType(false); }
  };

  // Antes el aviso de "esto te pisa texto ya escrito" era un
  // window.confirm nativo del navegador — inconsistente con el resto de la
  // app (que usa Dialog de MUI en todos lados) y con un mensaje genérico
  // que no decía cuántos campos se perderían. pendingTemplate guarda la
  // plantilla+datos parseados mientras se confirma en el Dialog de abajo.
  const [pendingTemplate, setPendingTemplate] = useState(null);

  const applyTemplate = (tmpl) => {
    if (!tmpl?.content) { setSelectedTemplate(tmpl || null); return; }
    let parsed;
    try { parsed = JSON.parse(tmpl.content); } catch { return; }

    const overwrittenCount = Object.keys(parsed).filter(
      (field) => form[field] && form[field] !== parsed[field]
    ).length;
    if (overwrittenCount > 0) {
      setPendingTemplate({ tmpl, parsed, overwrittenCount });
      return;
    }

    setForm(prev => ({ ...prev, ...parsed }));
    setSelectedTemplate(tmpl);
    setIsDirty(true);
  };

  const confirmApplyPendingTemplate = () => {
    if (!pendingTemplate) return;
    setForm(prev => ({ ...prev, ...pendingTemplate.parsed }));
    setSelectedTemplate(pendingTemplate.tmpl);
    setIsDirty(true);
    setPendingTemplate(null);
  };

  const autoSave = useCallback(async () => {
    if (!isDirty || !selectedDoctorId) return;
    const payload = { ...form, notes: sanitizeNotesHtml(form.notes), doctor_id: Number(selectedDoctorId), template_id: selectedTemplate?.id || null, problem_id: selectedProblemId ? Number(selectedProblemId) : null, appointment_id: appointmentId || null };
    try {
      if (!draftId) {
        const res = await createMedicalRecord(patient.id, payload);
        setDraftId(res.data.id);
      } else {
        await updateMedicalRecord(patient.id, draftId, payload);
      }
      setIsDirty(false);
      setLastSaved(new Date());
    } catch { /* falla silenciosa — el médico puede guardar manual */ }
  }, [isDirty, selectedDoctorId, selectedProblemId, form, selectedTemplate, draftId, patient.id, appointmentId]);

  useEffect(() => {
    const timer = setInterval(autoSave, 30000);
    return () => clearInterval(timer);
  }, [autoSave]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSaveRef.current?.();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const handleSaveRef = useRef(null);

  // Plantillas: priorizar las que matchean la especialidad del médico
  // seleccionado, pero sin ocultar el resto — clínicas chicas comparten
  // plantillas entre especialidades a propósito.
  const selectedDoctorSpecialty = doctors.find(d => d.id === Number(selectedDoctorId))?.specialty || null;
  const sortedTemplates = [...templates].sort((a, b) => {
    const aMatch = selectedDoctorSpecialty && a.specialty_tag === selectedDoctorSpecialty;
    const bMatch = selectedDoctorSpecialty && b.specialty_tag === selectedDoctorSpecialty;
    if (aMatch && !bMatch) return -1;
    if (!aMatch && bMatch) return 1;
    return 0;
  });

  const bmi = calcBMI(form.vital_weight_kg, form.vital_height_cm);
  const hasVitals = !!(form.vital_weight_kg || form.vital_height_cm || form.vital_blood_pressure
    || form.vital_heart_rate || form.vital_temperature_c || form.vital_oxygen_sat);
  const gestAge = calcEGAFromMethod({ method: egaMethod, ...egaInputs });
  // Cualquier edición manual del médico (cambiar de método o tocar un
  // campo) apaga el aviso de "embarazo en curso" — a partir de ahí es un
  // dato que el médico cargó/corrigió a mano, no el auto-completado.
  const setEga = (field) => (e) => { setUsingOngoingPregnancy(false); setEgaInputs(p => ({ ...p, [field]: e.target.value })); };
  const setEgaMethodManual = (method) => { setUsingOngoingPregnancy(false); setEgaMethod(method); };
  const loadNewPregnancyData = () => {
    setUsingOngoingPregnancy(false);
    setEgaMethod('fum');
    setEgaInputs({ fum: '', sonoDate: '', sonoWeeks: '', sonoDays: '0', crl: '', edd: '' });
  };

  const { features: clinicFeatures } = useClinicFeatures();
  const obstetricsEnabled = !!clinicFeatures?.obstetrics;
  const setBiometry = (field) => (e) => setForm(p => ({ ...p, [field]: e.target.value }));
  const efwGrams = calcEFW({ bpd: form.vital_fetal_bpd_mm, hc: form.vital_fetal_hc_mm, ac: form.vital_fetal_ac_mm, fl: form.vital_fetal_fl_mm });
  const efwPercentile = efwGrams && gestAge ? estimateEfwPercentile(efwGrams, gestAge.weeks + gestAge.days / 7) : null;
  const efwGrowth = efwGrowthLabel(efwPercentile);
  const togglePreeclampsiaFactor = (key) => setPreeclampsiaFactors(p => ({ ...p, [key]: !p[key] }));
  const preeclampsiaRisk = calcPreeclampsiaRisk(preeclampsiaFactors);

  // vital_fetal_efw_g se recalcula automáticamente a partir de las medidas
  // y se guarda junto con el resto del form — así queda persistido para
  // graficar la evolución sin que el médico tenga que tipearlo aparte.
  useEffect(() => {
    const rounded = efwGrams ? String(efwGrams) : '';
    setForm(p => (p.vital_fetal_efw_g === rounded ? p : { ...p, vital_fetal_efw_g: rounded }));
  }, [efwGrams]);

  // El checklist de preeclampsia se guarda junto con el resto del form —
  // antes vivía solo en estado local y se perdía al guardar la consulta.
  useEffect(() => {
    const checkedKeys = Object.keys(preeclampsiaFactors).filter(k => preeclampsiaFactors[k]);
    const serialized = checkedKeys.length > 0 ? JSON.stringify(checkedKeys) : '';
    setForm(p => (p.preeclampsia_risk_factors === serialized ? p : { ...p, preeclampsia_risk_factors: serialized }));
  }, [preeclampsiaFactors]);

  // La edad gestacional/FPP calculada por la calculadora se perdía al
  // guardar la consulta (vivía solo en egaMethod/egaInputs, nunca se
  // copiaba a `form`) — mismo patrón de sincronización que efwGrams/
  // preeclampsiaFactors de arriba. Se guarda como snapshot de ESTA
  // consulta puntual (no se recalcula después).
  useEffect(() => {
    setForm(p => {
      const weeks = gestAge ? String(gestAge.weeks) : '';
      const days = gestAge ? String(gestAge.days) : '';
      const fpp = gestAge ? gestAge.fpp : '';
      if (p.gest_age_weeks === weeks && p.gest_age_days === days && p.edd_date === fpp) return p;
      return { ...p, gest_age_weeks: weeks, gest_age_days: days, edd_date: fpp };
    });
  }, [gestAge?.weeks, gestAge?.days, gestAge?.fpp]);

  const handleAddFiles = (newFiles) => {
    setPendingFiles(prev => [...prev, ...newFiles]);
  };

  const handleSave = async () => {
    if (!selectedDoctorId) { setError('Seleccioná un médico'); return; }
    setSaving(true); setError('');
    try {
      setSaveStatus('record');
      const payload = {
        ...form,
        notes: sanitizeNotesHtml(form.notes),
        doctor_id: Number(selectedDoctorId),
        template_id: selectedTemplate?.id || null,
        problem_id: selectedProblemId ? Number(selectedProblemId) : null,
        appointment_id: appointmentId || null,
        status: 'finalizado',
      };
      let savedId;
      if (draftId) {
        await updateMedicalRecord(patient.id, draftId, payload);
        savedId = draftId;
      } else {
        const res = await createMedicalRecord(patient.id, payload);
        savedId = res.data?.id;
      }
      if (pendingFiles.length > 0 && savedId) {
        setSaveStatus('files');
        for (const file of pendingFiles) {
          try { await uploadRecordFile(savedId, file); } catch { }
        }
      }
      setForm(emptyRecord); setSelectedTemplate(null); setSelectedProblemId(''); setEgaInputs({ fum: '', sonoDate: '', sonoWeeks: '', sonoDays: '0', crl: '', edd: '' }); setPendingFiles([]);
      setDraftId(null); setIsDirty(false); setLastSaved(null);
      onSaved();
    } catch (err) {
      setError(err.userMessage || err.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); setSaveStatus(''); }
  };
  handleSaveRef.current = handleSave;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {error && <Alert severity="error">{error}</Alert>}
      {appointmentId && (
        <Alert severity="info" sx={{ py: 0.5 }}>
          Esta consulta va a quedar vinculada al turno de hoy.
          {appointmentNote && (
            <><br /><strong>Nota del turno:</strong> {appointmentNote}</>
          )}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        {!isMedico ? (
          <FormControl sx={{ minWidth: { xs: '100%', sm: 200 }, flex: 1 }} error={!selectedDoctorId && !!error}>
            <InputLabel>Médico *</InputLabel>
            <Select value={selectedDoctorId} label="Médico *" onChange={e => { setSelectedDoctorId(e.target.value); setError(''); }}>
              {doctors.map(d => <MenuItem key={d.id} value={d.id}>{d.name}{d.specialty ? ` — ${d.specialty}` : ''}</MenuItem>)}
            </Select>
            {!selectedDoctorId && !!error && <FormHelperText>{error}</FormHelperText>}
          </FormControl>
        ) : (
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">Médico</Typography>
            <Typography variant="body1" fontWeight={500}>{user?.username}</Typography>
          </Box>
        )}
        <FormControl sx={{ minWidth: { xs: '100%', sm: 200 }, flex: 1 }}>
          <InputLabel>Usar plantilla</InputLabel>
          <Select value={selectedTemplate?.id || ''} label="Usar plantilla"
            onChange={e => applyTemplate(sortedTemplates.find(t => t.id === e.target.value))}>
            <MenuItem value=""><em>Sin plantilla</em></MenuItem>
            {sortedTemplates.map(t => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
                {t.specialty_tag && (
                  <Chip
                    label={t.specialty_tag}
                    size="small"
                    sx={{ ml: 1 }}
                    color={t.specialty_tag === selectedDoctorSpecialty ? 'success' : 'default'}
                    variant={t.specialty_tag === selectedDoctorSpecialty ? 'filled' : 'outlined'}
                  />
                )}
                {t.scope === 'organizational' && <Chip label="Org." size="small" sx={{ ml: 1 }} color="primary" />}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        <Autocomplete
          freeSolo
          sx={{ flex: 1, minWidth: { xs: '100%', sm: 180 } }}
          size="medium"
          options={consultationTypes.map(t => t.name)}
          value={form.consultation_type || ''}
          onChange={(_, newValue) => handleConsultationTypeChange(newValue || '')}
          onInputChange={(_, newValue, reason) => {
            // "reset" dispara cuando el usuario selecciona una opción de la
            // lista (ya cubierto por onChange arriba) o cuando el Autocomplete
            // sincroniza el input con `value` — evita duplicar la escritura
            // libre con la selección de una opción existente.
            if (reason === 'input') setForm(prev => ({ ...prev, consultation_type: newValue }));
          }}
          onBlur={() => handleConsultationTypeChange(form.consultation_type || '')}
          renderInput={(params) => (
            <TextField {...params} label="Tipo de consulta"
              placeholder="Buscar o escribir uno nuevo…"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {creatingConsultationType && <CircularProgress size={16} sx={{ mr: 2 }} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        {problemsProp.length > 0 && (
          <FormControl sx={{ flex: 1, minWidth: { xs: '100%', sm: 180 } }}>
            <InputLabel>Problema vinculado</InputLabel>
            <Select value={selectedProblemId} label="Problema vinculado" onChange={e => setSelectedProblemId(e.target.value)}>
              <MenuItem value=""><em>Sin vincular</em></MenuItem>
              {problemsProp.filter(p => p.status !== 'resuelto').map(p => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                    ({p.status})
                  </Typography>
                </MenuItem>
              ))}
              {problemsProp.some(p => p.status === 'resuelto') && [
                <MenuItem key="__divider__" disabled sx={{ fontSize: '0.72rem', color: 'text.disabled' }}>— Resueltos —</MenuItem>,
                ...problemsProp.filter(p => p.status === 'resuelto').map(p => (
                  <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                )),
              ]}
            </Select>
          </FormControl>
        )}
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        Evolución
      </Typography>
      <RichTextEditor
        value={form.notes}
        onChange={(html) => { setForm(prev => ({ ...prev, notes: html })); setIsDirty(true); }}
        placeholder="Motivo de consulta, examen físico, diagnóstico, tratamiento — todo lo que necesites anotar de esta consulta."
        minRows={8}
      />

      {/* Signos vitales — antes tenía la misma jerarquía visual (acordeón
          colapsado, mismo peso de texto) que cualquier sección opcional de
          abajo, sin distinguirse de "Calculadora de edad gestacional" (que
          sí es nicho, solo obstetricia). Pedido explícito del usuario:
          destacar lo clínicamente relevante sobre lo opcional. Acá eso se
          traduce en: ícono + color de acento en el header (en vez de texto
          plano), y el acordeón arranca expandido mientras ningún campo
          tenga datos todavía — una vez que el médico cargó algo, no hace
          falta forzarlo abierto en cada consulta nueva (se vuelve a
          colapsar solo por defecto en la próxima). No se lo vuelve
          obligatorio: sigue siendo opcional completar, solo más visible. */}
      <Accordion defaultExpanded={!hasVitals}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{
          bgcolor: !hasVitals ? 'primary.50' : 'transparent',
          borderRadius: !hasVitals ? '4px 4px 0 0' : 0,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <MonitorHeartIcon fontSize="small" color="primary" />
            <Typography fontWeight={600}>Signos vitales</Typography>
            {!hasVitals && (
              <Typography variant="caption" color="primary.main" sx={{ ml: 0.5 }}>— sin completar</Typography>
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={1.5}>
            {[
              { field: 'vital_weight_kg', label: 'Peso (kg)' },
              { field: 'vital_height_cm', label: 'Talla (cm)', helperText: 'ej: 165' },
              { field: 'vital_blood_pressure', label: 'Tensión arterial' }, { field: 'vital_heart_rate', label: 'FC (lpm)' },
              { field: 'vital_temperature_c', label: 'Temperatura (°C)' }, { field: 'vital_oxygen_sat', label: 'SatO₂ (%)' },
            ].map(({ field, label, helperText }) => (
              <Grid size={{ xs: 12, sm: 6 }} key={field}>
                <TextField fullWidth size="small" label={label} value={form[field]} onChange={set(field)} helperText={helperText} />
              </Grid>
            ))}
          </Grid>
          {bmi && (
            <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">IMC:</Typography>
              <Chip label={`${bmi.value} — ${bmi.label}`}
                color={bmi.label === 'Normal' ? 'success' : bmi.label === 'Bajo peso' ? 'warning' : 'error'} size="small" />
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Calculadora edad gestacional (+ biometría fetal y riesgo de
          preeclampsia si el flag "obstetrics" está activo) */}
      <Accordion defaultExpanded={usingOngoingPregnancy}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>
            Calculadora de edad gestacional{obstetricsEnabled ? ' y obstetricia' : ''}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {/* Embarazo en curso detectado: ya hay una FPP vigente guardada en
              una consulta anterior — se precarga sola (ver ongoingPregnancy
              más arriba) para no pedirle al médico que vuelva a cargar el
              dato ancla en cada control. Si no es el mismo embarazo, un
              click alcanza para volver a cargar de cero. */}
          {usingOngoingPregnancy && (
            <Alert severity="info" sx={{ py: 0.5 }}
              action={
                <Button size="small" onClick={loadNewPregnancyData} sx={{ whiteSpace: 'nowrap' }}>
                  Es otro embarazo
                </Button>
              }>
              Embarazo en curso (FPP: {ongoingPregnancy.eddDisplay}) — edad gestacional recalculada automáticamente.
            </Alert>
          )}

          <FormControl size="small" fullWidth>
            <InputLabel>Método</InputLabel>
            <Select value={egaMethod} label="Método" onChange={e => setEgaMethodManual(e.target.value)}>
              <MenuItem value="fum">FUM — Fecha última menstruación</MenuItem>
              <MenuItem value="sono">Ecografía — fecha + EGA ecográfica</MenuItem>
              <MenuItem value="crl">CRL — Longitud cráneo-caudal</MenuItem>
              <MenuItem value="edd">FPP — Fecha probable de parto</MenuItem>
            </Select>
          </FormControl>

          {egaMethod === 'fum' && (
            <TextField fullWidth size="small" type="date" label="Fecha FUM" InputLabelProps={{ shrink: true }}
              value={egaInputs.fum} onChange={setEga('fum')} />
          )}

          {egaMethod === 'sono' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <TextField fullWidth size="small" type="date" label="Fecha de la ecografía" InputLabelProps={{ shrink: true }}
                value={egaInputs.sonoDate} onChange={setEga('sonoDate')} />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField size="small" label="Semanas" type="number" inputProps={{ min: 4, max: 42 }}
                  value={egaInputs.sonoWeeks} onChange={setEga('sonoWeeks')} sx={{ flex: 1 }} />
                <TextField size="small" label="Días" type="number" inputProps={{ min: 0, max: 6 }}
                  value={egaInputs.sonoDays} onChange={setEga('sonoDays')} sx={{ flex: 1 }} />
              </Box>
            </Box>
          )}

          {egaMethod === 'crl' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <TextField fullWidth size="small" type="date" label="Fecha de la ecografía" InputLabelProps={{ shrink: true }}
                value={egaInputs.sonoDate} onChange={setEga('sonoDate')} />
              <TextField fullWidth size="small" label="CRL (mm)" type="number" inputProps={{ min: 1, max: 84 }}
                helperText="Longitud cráneo-caudal en mm (rango válido: 1–84 mm)"
                value={egaInputs.crl} onChange={setEga('crl')} />
            </Box>
          )}

          {egaMethod === 'edd' && (
            <TextField fullWidth size="small" type="date" label="Fecha probable de parto (FPP)" InputLabelProps={{ shrink: true }}
              value={egaInputs.edd} onChange={setEga('edd')} />
          )}

          {gestAge && (
            <Box sx={{ p: 1.5, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.200' }}>
              <Typography variant="body1" fontWeight={600}>{gestAge.weeks} semanas y {gestAge.days} días</Typography>
              <Typography variant="body2" color="text.secondary">FPP estimada: {gestAge.fpp}</Typography>
            </Box>
          )}

          {/* Calculadoras de obstetricia — feature nicho, solo visible con
              el flag "obstetrics" activo (gineco-obstetricia). Viven acá
              adentro para no ocupar lugar en la HC de otras especialidades. */}
          {obstetricsEnabled && (
            <>
              <Divider sx={{ my: 0.5 }} />

              <Box>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Biometría fetal — peso estimado
                </Typography>
                <Grid container spacing={1}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField fullWidth size="small" label="DBP (mm)" type="number"
                      value={form.vital_fetal_bpd_mm} onChange={setBiometry('vital_fetal_bpd_mm')} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField fullWidth size="small" label="CC (mm)" type="number"
                      value={form.vital_fetal_hc_mm} onChange={setBiometry('vital_fetal_hc_mm')} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField fullWidth size="small" label="CA (mm)" type="number"
                      value={form.vital_fetal_ac_mm} onChange={setBiometry('vital_fetal_ac_mm')} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField fullWidth size="small" label="LF (mm)" type="number"
                      value={form.vital_fetal_fl_mm} onChange={setBiometry('vital_fetal_fl_mm')} />
                  </Grid>
                </Grid>
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                  DBP/CC/CA/LF en milímetros. Fórmula de Hadlock — estimación de uso general, no reemplaza el software FMF certificado.
                </Typography>

                {efwGrams && (
                  <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.200' }}>
                    <Typography variant="body1" fontWeight={600}>
                      Peso fetal estimado: {efwGrams.toLocaleString('es-AR')} g
                    </Typography>
                    {efwPercentile != null ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          Percentilo {efwPercentile} para {gestAge?.weeks} sem
                        </Typography>
                        {efwGrowth && <Chip label={efwGrowth.label} size="small" color={efwGrowth.color} />}
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.disabled">
                        Cargá la edad gestacional (arriba) para calcular el percentilo.
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 0.5 }} />

              <Box>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                  Factores de riesgo de preeclampsia
                </Typography>
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 1 }}>
                  Checklist ACOG/NICE — no es un score numérico ni el algoritmo FMF. 1 factor de alto riesgo o 2+ de riesgo moderado sugieren considerar aspirina en dosis baja (idealmente antes de sem 16).
                </Typography>

                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  ALTO RIESGO
                </Typography>
                <FormGroup>
                  {PREECLAMPSIA_HIGH_RISK.map(f => (
                    <FormControlLabel key={f.key} sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.85rem' } }}
                      control={<Checkbox size="small" checked={!!preeclampsiaFactors[f.key]} onChange={() => togglePreeclampsiaFactor(f.key)} />}
                      label={f.label}
                    />
                  ))}
                </FormGroup>

                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  RIESGO MODERADO
                </Typography>
                <FormGroup>
                  {PREECLAMPSIA_MODERATE_RISK.map(f => (
                    <FormControlLabel key={f.key} sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.85rem' } }}
                      control={<Checkbox size="small" checked={!!preeclampsiaFactors[f.key]} onChange={() => togglePreeclampsiaFactor(f.key)} />}
                      label={f.label}
                    />
                  ))}
                </FormGroup>

                {(preeclampsiaRisk.highCount > 0 || preeclampsiaRisk.moderateCount > 0) && (
                  <Box sx={{
                    mt: 1, p: 1.5, borderRadius: 1, border: '1px solid',
                    bgcolor: preeclampsiaRisk.suggestAspirin ? 'error.50' : 'grey.50',
                    borderColor: preeclampsiaRisk.suggestAspirin ? 'error.200' : 'divider',
                  }}>
                    <Typography variant="body2" fontWeight={600} color={preeclampsiaRisk.suggestAspirin ? 'error.main' : 'text.primary'}>
                      {preeclampsiaRisk.suggestAspirin
                        ? 'Considerar aspirina en dosis baja (12–28 sem)'
                        : 'Por debajo del umbral de indicación de aspirina'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {preeclampsiaRisk.highCount} factor{preeclampsiaRisk.highCount !== 1 ? 'es' : ''} de alto riesgo, {preeclampsiaRisk.moderateCount} de riesgo moderado.
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Archivos */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Archivos a adjuntar {pendingFiles.length > 0 && `(${pendingFiles.length})`}
          </Typography>
          <AttachFileButton onFilesSelected={handleAddFiles} onFilesRejected={setError} multiple label="Seleccionar" />
        </Box>
        {pendingFiles.length === 0 ? (
          <Typography variant="caption" color="text.secondary">Los archivos se subirán al guardar.</Typography>
        ) : (
          <Stack spacing={0.5}>
            {pendingFiles.map((f, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 0.75,
                border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Box sx={{ color: 'text.secondary', display: 'flex' }}>{FILE_ICON(f.type)}</Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="caption" noWrap>{f.name}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{formatBytes(f.size)}</Typography>
                </Box>
                <IconButton size="small" onClick={() => setPendingFiles(p => p.filter((_, j) => j !== i))} disabled={saving}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Stack>
        )}
        {saving && saveStatus === 'files' && <LinearProgress sx={{ mt: 1 }} />}
      </Box>

      {/* En mobile el texto de estado (puede ser largo: "Borrador guardado
          HH:MM — hacé clic en Guardar...") pasa a su propia línea arriba, y
          los botones abajo ocupan el ancho completo — más fácil de tocar y
          ya no depende del flexWrap accidental para no apretarse. */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1 }}>
        <Typography variant="caption" color={isDirty ? 'warning.main' : lastSaved ? 'info.main' : 'transparent'}>
          {isDirty
            ? 'Cambios sin guardar'
            : lastSaved
              ? `Borrador guardado ${lastSaved.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })} — hacé clic en Guardar para finalizar la consulta`
              : '.'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onCancel} disabled={saving} fullWidth={isMobile}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving} fullWidth={isMobile}>
            {saving
              ? <><CircularProgress size={16} sx={{ mr: 1 }} />{saveStatus === 'files' ? 'Subiendo archivos...' : 'Guardando...'}</>
              : 'Guardar registro'}
          </Button>
        </Box>
      </Box>

      <Dialog open={!!pendingTemplate} onClose={() => setPendingTemplate(null)} maxWidth="xs" fullWidth>
        <DialogTitle>¿Sobreescribir con la plantilla?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            "{pendingTemplate?.tmpl?.name}" va a reemplazar el texto que ya escribiste en{' '}
            {pendingTemplate?.overwrittenCount} campo{pendingTemplate?.overwrittenCount === 1 ? '' : 's'}.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingTemplate(null)}>Cancelar</Button>
          <Button variant="contained" onClick={confirmApplyPendingTemplate}>Sobreescribir</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ── ResumenTab ────────────────────────────────────────────────────────────

const EMPTY_PROBLEM_FORM = { name: '', status: 'activo', notes: '' };

const ResumenTab = ({ records, patient, onNewRecord, onGoToConsultas, studies: externalStudies, onGoToEstudios, compact = false, externalProblems, onProblemsChanged, onStudiesChanged }) => {
  const { isAdmin, isMedico } = useAuth();
  const notify = useNotification();
  const canManage = isAdmin || isMedico;
  const { features: clinicFeatures } = useClinicFeatures();
  const obstetricsEnabled = !!clinicFeatures?.obstetrics;

  // En modo compact el padre ya tiene los problemas y los pasa via externalProblems
  const [internalProblems, setInternalProblems] = useState([]);
  const problems = externalProblems !== undefined ? externalProblems : internalProblems;

  // Mismo patrón que problems: MedicalRecordPanel (Dialog) ya trae los
  // estudios cargados y los pasa por prop; PatientSidebar (perfil de
  // paciente) no los tiene, así que se cargan acá mismo en ese caso.
  const [internalStudies, setInternalStudies] = useState([]);
  const studies = externalStudies !== undefined ? externalStudies : internalStudies;

  const loadStudies = useCallback(async () => {
    if (!patient?.id || externalStudies !== undefined) return;
    try { setInternalStudies((await getPatientStudies(patient.id)).data || []); } catch { }
  }, [patient?.id, externalStudies]);

  useEffect(() => { loadStudies(); }, [loadStudies]);

  // "Estudios recientes" acá reusa StudyRow en modo compact — antes no
  // pasaba onRename (quedaba en modo solo-lectura, mismo gate que
  // onDelete) y el rename solo funcionaba entrando a la pestaña "Estudios"
  // completa. Pedido explícito del usuario: que funcione también desde
  // este resumen. Mismo patrón que refreshProblems: si los estudios vienen
  // por prop (externalStudies, caso MedicalRecordPanel) se avisa al padre
  // para que actualice su propio estado; si son internos (caso
  // PatientSidebar) se actualiza aquí mismo sin ida y vuelta al backend.
  const handleStudyRename = (studyId, newName) => {
    if (externalStudies !== undefined) {
      onStudiesChanged?.(studyId, newName);
    } else {
      setInternalStudies(prev => prev.map(s => s.id === studyId ? { ...s, description: newName } : s));
    }
  };

  const [problemDialogOpen, setProblemDialogOpen] = useState(false);
  const [editingProblem, setEditingProblem] = useState(null);
  const [problemForm, setProblemForm] = useState(EMPTY_PROBLEM_FORM);
  const [savingProblem, setSavingProblem] = useState(false);

  const [summary, setSummary] = useState(patient?.clinical_summary || '');
  const [savingSummary, setSavingSummary] = useState(false);
  // El botón de guardado quedaba siempre habilitado, sin distinguir "no
  // toqué nada" de "edité pero no guardé" — pedido explícito: confundía si
  // hacía falta tocar el botón o el resumen se guardaba solo. Se compara
  // contra `savedSummary` (no contra patient.clinical_summary directo)
  // porque el `patient` que llega por prop no se refresca solo tras guardar
  // — sin este estado propio, el botón seguiría habilitado después de un
  // guardado exitoso hasta que el padre recargue al paciente.
  const [savedSummary, setSavedSummary] = useState(patient?.clinical_summary || '');
  const summaryDirty = summary !== savedSummary;

  const loadProblems = useCallback(async () => {
    if (!patient?.id || externalProblems !== undefined) return;
    try { setInternalProblems((await getPatientProblems(patient.id)).data || []); } catch {}
  }, [patient?.id, externalProblems]);

  const refreshProblems = async () => {
    if (externalProblems !== undefined) {
      onProblemsChanged?.();
    } else {
      await loadProblems();
    }
  };

  useEffect(() => { loadProblems(); }, [loadProblems]);
  useEffect(() => {
    setSummary(patient?.clinical_summary || '');
    setSavedSummary(patient?.clinical_summary || '');
  }, [patient?.clinical_summary]);

  const openAdd = () => {
    setEditingProblem(null); setProblemForm(EMPTY_PROBLEM_FORM); setProblemDialogOpen(true);
  };
  const openEdit = (p) => {
    setEditingProblem(p); setProblemForm({ name: p.name, status: p.status, notes: p.notes || '' });
    setProblemDialogOpen(true);
  };

  const handleSaveProblem = async () => {
    if (!problemForm.name.trim()) return;
    setSavingProblem(true);
    try {
      if (editingProblem) {
        await updatePatientProblem(patient.id, editingProblem.id, problemForm);
      } else {
        await createPatientProblem(patient.id, problemForm);
      }
      await refreshProblems();
      setProblemDialogOpen(false);
    } catch (err) { notify(err.userMessage || 'Error al guardar', 'error'); }
    finally { setSavingProblem(false); }
  };

  const deleteProblemConfirm = useConfirmDialog();

  const confirmDeleteProblem = async (p) => {
    deleteProblemConfirm.close();
    try { await deletePatientProblem(patient.id, p.id); await refreshProblems(); }
    catch (err) { notify(err.userMessage || 'Error al eliminar', 'error'); }
  };

  const handleSaveSummary = async () => {
    setSavingSummary(true);
    try {
      await updatePatient(patient.id, { clinical_summary: summary });
      setSavedSummary(summary);
      notify('Resumen actualizado', 'success');
    } catch (err) { notify(err.userMessage || 'Error al actualizar', 'error'); }
    finally { setSavingSummary(false); }
  };

  const lastRecords = records.slice(0, 5);
  const lastStudies = studies.slice(0, 4);
  // Preview de estudios desde "Estudios recientes" (sidebar) — navega entre
  // TODOS los estudios previsualizables del paciente, no solo los 4 que se
  // muestran acá, para que las flechas del visor tengan sentido.
  const [studiesViewerIndex, setStudiesViewerIndex] = useState(null);
  const viewableStudies = studies
    .filter(s => isPreviewable(s.mime_type))
    .map(s => ({ ...s, _kind: 'study' }));
  const handleStudyPreview = (study) => setStudiesViewerIndex(viewableStudies.findIndex(s => s.id === study.id));

  // IMC calculado del registro más reciente que tenga peso Y altura
  // cargados (no necesariamente el último, si esa consulta no los tenía) —
  // pedido explícito: que el IMC se calcule solo y se vea en la ficha.
  const latestVitalsRecord = records.find(r => r.vital_weight_kg && r.vital_height_cm);
  const latestBmi = latestVitalsRecord
    ? calcBMI(latestVitalsRecord.vital_weight_kg, latestVitalsRecord.vital_height_cm)
    : null;

  // En modo compact (sidebar) solo muestra problemas + resumen clínico
  if (compact) {
    return (
      <Box>
        <Box sx={{ mb: 2 }}>
          <ProblemsList problems={problems} canManage={canManage} onAdd={openAdd} onEdit={openEdit} onDelete={deleteProblemConfirm.open} />
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Estudios complementarios — pantallazo rápido sin entrar a
            evoluciones, pedido explícito: acceder a la última eco/análisis
            sin tener que leer las notas de la consulta donde se subió. */}
        {studies.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" fontWeight={700} color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem' }}>
                Estudios recientes
              </Typography>
              {onGoToEstudios && studies.length > 4 && (
                <Button size="small" sx={{ minWidth: 0, p: 0, fontSize: '0.7rem' }} onClick={onGoToEstudios}>
                  Ver todos ({studies.length})
                </Button>
              )}
            </Box>
            <Stack spacing={0.75}>
              {lastStudies.map(s => <StudyRow key={s.id} study={s} compact onPreview={handleStudyPreview} onRename={canManage ? handleStudyRename : null} />)}
            </Stack>
            <Divider sx={{ mt: 1.5 }} />
          </Box>
        )}

        {/* Medicación actual */}
        {records.length > 0 && (() => {
          const lastWithMeds = records.find(r => r.treatment);
          return lastWithMeds ? (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2" fontWeight={700} color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem', mb: 0.75 }}>
                Último tratamiento
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                {lastWithMeds.treatment}
              </Typography>
              <Divider sx={{ mt: 1.5, mb: 1.5 }} />
            </Box>
          ) : null;
        })()}

        {/* Resumen clínico */}
        <Box>
          <Typography variant="subtitle2" fontWeight={700} color="text.secondary"
            sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem', mb: 0.75 }}>
            Resumen clínico
          </Typography>
          <ClinicalSummaryEditor
            canManage={canManage} summary={summary} onChange={setSummary}
            onSave={handleSaveSummary} saving={savingSummary} dirty={summaryDirty}
            placeholder="Contexto general del paciente..."
          />
        </Box>

        {/* VitalsChart compacto si hay datos */}
        {records.some(r => r.vital_weight_kg || r.vital_blood_pressure || r.vital_heart_rate) && (
          <>
            <Divider sx={{ my: 1.5 }} />
            {latestBmi && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="caption" color="text.secondary">IMC actual:</Typography>
                <Chip label={`${latestBmi.value} — ${latestBmi.label}`} size="small"
                  color={latestBmi.label === 'Normal' ? 'success' : latestBmi.label === 'Bajo peso' ? 'warning' : 'error'} />
              </Box>
            )}
            <VitalsChart records={records} />
            {obstetricsEnabled && records.some(r => r.vital_fetal_efw_g) && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <FetalGrowthChart records={records} />
              </>
            )}
          </>
        )}

        {/* Dialog agregar/editar problema */}
        <Dialog open={problemDialogOpen} onClose={() => setProblemDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>{editingProblem ? 'Editar problema' : 'Agregar problema'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              <TextField label="Problema *" fullWidth size="small" value={problemForm.name}
                onChange={e => setProblemForm(p => ({ ...p, name: e.target.value }))}
                placeholder="Ej: Diabetes tipo 2, HTA..." />
              <FormControl fullWidth size="small">
                <InputLabel>Estado</InputLabel>
                <Select value={problemForm.status} label="Estado"
                  onChange={e => setProblemForm(p => ({ ...p, status: e.target.value }))}>
                  <MenuItem value="activo">Activo</MenuItem>
                  <MenuItem value="cronico">Crónico</MenuItem>
                  <MenuItem value="resuelto">Resuelto</MenuItem>
                </Select>
              </FormControl>
              <TextField label="Notas (opcional)" fullWidth size="small" multiline rows={2}
                value={problemForm.notes} onChange={e => setProblemForm(p => ({ ...p, notes: e.target.value }))} />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setProblemDialogOpen(false)}>Cancelar</Button>
            <Button variant="contained" onClick={handleSaveProblem}
              disabled={savingProblem || !problemForm.name.trim()}>
              {savingProblem ? <CircularProgress size={16} /> : editingProblem ? 'Guardar' : 'Agregar'}
            </Button>
          </DialogActions>
        </Dialog>

        <ConfirmDialog
          open={!!deleteProblemConfirm.pending}
          title="Eliminar problema"
          message={`¿Eliminar "${deleteProblemConfirm.pending?.name}"?`}
          onCancel={deleteProblemConfirm.close}
          onConfirm={() => confirmDeleteProblem(deleteProblemConfirm.pending)}
        />

        {studiesViewerIndex !== null && (
          <RecordFileViewer
            files={viewableStudies}
            index={studiesViewerIndex}
            onIndexChange={setStudiesViewerIndex}
            onClose={() => setStudiesViewerIndex(null)}
            onDownload={(f) => downloadPatientStudy(f.id).then((res) => downloadBlobAsFile(res.data, f.original_filename, f.mime_type))}
          />
        )}
      </Box>
    );
  }

  return (
    <Grid container spacing={2.5}>
      {/* LEFT: Problemas + Resumen clínico */}
      <Grid size={{ xs: 12, md: 5 }}>
        <Box sx={{ mb: 2.5 }}>
          <ProblemsList problems={problems} canManage={canManage} onAdd={openAdd} onEdit={openEdit} onDelete={deleteProblemConfirm.open} />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Resumen clínico */}
        <Box>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Resumen clínico</Typography>
          <ClinicalSummaryEditor
            canManage={canManage} summary={summary} onChange={setSummary}
            onSave={handleSaveSummary} saving={savingSummary} dirty={summaryDirty}
            placeholder="Resumen clínico general del paciente, observaciones persistentes..."
          />
        </Box>
      </Grid>

      {/* RIGHT: Estudios + Últimas consultas + medicación */}
      <Grid size={{ xs: 12, md: 7 }}>
        {studies.length > 0 && (
          <Box sx={{ mb: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={700}>Estudios recientes</Typography>
              {onGoToEstudios && (
                <Button size="small" onClick={onGoToEstudios}>Ver todos ({studies.length})</Button>
              )}
            </Box>
            <Stack spacing={1}>
              {lastStudies.map(s => <StudyRow key={s.id} study={s} compact onPreview={handleStudyPreview} onRename={canManage ? handleStudyRename : null} />)}
            </Stack>
          </Box>
        )}

        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={700}>Últimas consultas</Typography>
            {records.length > 5 && (
              <Button size="small" onClick={onGoToConsultas}>Ver todas ({records.length})</Button>
            )}
          </Box>
          {lastRecords.length === 0 ? (
            <Typography variant="body2" color="text.secondary">Sin consultas registradas.</Typography>
          ) : (
            <Stack spacing={1}>
              {lastRecords.map(r => <RecordCardCompact key={r.id} record={r} onClick={onGoToConsultas} />)}
            </Stack>
          )}
          <Button startIcon={<AddIcon />} variant="outlined" size="small" sx={{ mt: 1.5 }} onClick={onNewRecord}>
            Nueva consulta
          </Button>
        </Box>

        {records.some(r => r.vital_weight_kg || r.vital_blood_pressure || r.vital_heart_rate) && (
          <>
            <Divider sx={{ my: 2 }} />
            {latestBmi && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Typography variant="body2" color="text.secondary">IMC actual:</Typography>
                <Chip label={`${latestBmi.value} — ${latestBmi.label}`} size="small"
                  color={latestBmi.label === 'Normal' ? 'success' : latestBmi.label === 'Bajo peso' ? 'warning' : 'error'} />
              </Box>
            )}
            <VitalsChart records={records} />
            {obstetricsEnabled && records.some(r => r.vital_fetal_efw_g) && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <FetalGrowthChart records={records} />
              </>
            )}
          </>
        )}

      </Grid>

      {/* Dialog agregar/editar problema */}
      <Dialog open={problemDialogOpen} onClose={() => setProblemDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editingProblem ? 'Editar problema' : 'Agregar problema'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              label="Problema *" fullWidth size="small"
              value={problemForm.name}
              onChange={e => setProblemForm(p => ({ ...p, name: e.target.value }))}
              placeholder="Ej: Diabetes tipo 2, HTA, Hipotiroidismo..."
            />
            <FormControl fullWidth size="small">
              <InputLabel>Estado</InputLabel>
              <Select value={problemForm.status} label="Estado"
                onChange={e => setProblemForm(p => ({ ...p, status: e.target.value }))}>
                <MenuItem value="activo">Activo</MenuItem>
                <MenuItem value="cronico">Crónico</MenuItem>
                <MenuItem value="resuelto">Resuelto</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Notas (opcional)" fullWidth size="small" multiline rows={2}
              value={problemForm.notes}
              onChange={e => setProblemForm(p => ({ ...p, notes: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProblemDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveProblem}
            disabled={savingProblem || !problemForm.name.trim()}>
            {savingProblem ? <CircularProgress size={16} /> : editingProblem ? 'Guardar' : 'Agregar'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteProblemConfirm.pending}
        title="Eliminar problema"
        message={`¿Eliminar "${deleteProblemConfirm.pending?.name}"?`}
        onCancel={deleteProblemConfirm.close}
        onConfirm={() => confirmDeleteProblem(deleteProblemConfirm.pending)}
      />

      {studiesViewerIndex !== null && (
        <RecordFileViewer
          files={viewableStudies}
          index={studiesViewerIndex}
          onIndexChange={setStudiesViewerIndex}
          onClose={() => setStudiesViewerIndex(null)}
          onDownload={(f) => downloadPatientStudy(f.id).then((res) => downloadBlobAsFile(res.data, f.original_filename, f.mime_type))}
        />
      )}
    </Grid>
  );
};

// ── ConsultasTab ──────────────────────────────────────────────────────────

// Debajo de este número de consultas, los chips de filtro por tipo no
// aportan nada — con pocas consultas alcanza con mirarlas todas, y el
// filtro solo agrega ruido visual a una lista ya corta.
const MIN_RECORDS_FOR_TYPE_FILTER = 6;

const ConsultasTab = ({
  records, patient, onRecordSaved, showFormInitially, activeProblem, onClearProblem, doctors, problems = [],
  initialAppointmentId = null, initialDoctorId = null, initialAppointmentNote = null,
  hasMoreRecords = false, loadingMoreRecords = false, onLoadMoreRecords = null,
}) => {
  const { isAdmin, isMedico } = useAuth();
  const [showForm, setShowForm] = useState(showFormInitially || false);
  const [expandedIndex, setExpandedIndex] = useState(0);
  const [filterType, setFilterType] = useState('');
  const canCreate = isAdmin || isMedico;
  const accordionRefs = useRef({});

  useEffect(() => { if (showFormInitially) setShowForm(true); }, [showFormInitially]);
  useEffect(() => { setExpandedIndex(0); }, [activeProblem, filterType]);

  const handleSaved = () => { setShowForm(false); onRecordSaved(); };

  // Consultas con autosave pero nunca finalizadas explícitamente — ver
  // comentario en RecordCardCompact. Se avisa agregado acá arriba porque
  // el chip "Borrador" de cada fila solo se nota si se escanea la lista
  // entera; esto la hace imposible de pasar por alto.
  const draftRecords = records.filter(r => r.status !== 'finalizado');

  // Filtro por problema: usa la FK explícita problem_id
  const problemFiltered = activeProblem
    ? records.filter(r => r.problem_id === activeProblem.id)
    : records;

  const filteredRecords = filterType
    ? problemFiltered.filter(r => r.consultation_type === filterType)
    : problemFiltered;

  // Counts sobre problemFiltered para que reflejen el filtro de problema activo
  const typeCounts = problemFiltered.reduce((acc, r) => {
    if (r.consultation_type) acc[r.consultation_type] = (acc[r.consultation_type] || 0) + 1;
    return acc;
  }, {});
  const typeOptions = Object.keys(typeCounts);

  const handleNav = (newIndex) => {
    if (newIndex < 0 || newIndex >= filteredRecords.length) return;
    setExpandedIndex(newIndex);
    const el = accordionRefs.current[newIndex];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  return (
    <Box>
      {canCreate && !showForm && (
        <Button startIcon={<AddIcon />} variant="contained" size="small" sx={{ mb: 2 }}
          onClick={() => setShowForm(true)}>
          Nueva consulta
        </Button>
      )}

      {showForm && (
        <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5, borderColor: 'primary.300', borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Nueva consulta</Typography>
          <NewRecordForm patient={patient} records={records} onSaved={handleSaved} onCancel={() => setShowForm(false)} doctors={doctors} problems={problems} initialProblemId={activeProblem?.id || ''} appointmentId={initialAppointmentId} initialDoctorId={initialDoctorId} appointmentNote={initialAppointmentNote} />
        </Paper>
      )}

      {!showForm && draftRecords.length > 0 && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, mb: 2, px: 1.5, py: 1,
          bgcolor: 'warning.light', border: '1px solid', borderColor: 'warning.main', borderRadius: 1.5,
        }}>
          <WarningAmberIcon fontSize="small" color="warning" />
          <Typography variant="body2" sx={{ flex: 1 }}>
            {draftRecords.length === 1
              ? 'Hay 1 consulta sin finalizar — el autoguardado no la cierra sola.'
              : `Hay ${draftRecords.length} consultas sin finalizar — el autoguardado no las cierra solas.`}
          </Typography>
        </Box>
      )}

      {/* Banner filtro activo por problema */}
      {activeProblem && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, px: 1.5, py: 0.75,
          bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200', borderRadius: 1.5,
        }}>
          <Typography variant="body2" sx={{ flex: 1 }}>
            Mostrando consultas relacionadas con <strong>{activeProblem.name}</strong>
            {' '}({problemFiltered.length} de {records.length})
          </Typography>
          <Chip label="Quitar filtro" size="small" onClick={onClearProblem} onDelete={onClearProblem} />
        </Box>
      )}

      {/* Filter chips por tipo — solo si hay consultas suficientes como
          para que valga la pena filtrar (ver MIN_RECORDS_FOR_TYPE_FILTER) */}
      {typeOptions.length > 0 && problemFiltered.length >= MIN_RECORDS_FOR_TYPE_FILTER && (
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2 }}>
          <Chip
            label={`Todas (${problemFiltered.length})`} size="small"
            onClick={() => { setFilterType(''); setExpandedIndex(0); }}
            color={filterType === '' ? 'primary' : 'default'}
            variant={filterType === '' ? 'filled' : 'outlined'}
          />
          {typeOptions.map(t => (
            <Chip
              key={t}
              label={`${t} (${typeCounts[t]})`} size="small"
              onClick={() => { setFilterType(t); setExpandedIndex(0); }}
              color={filterType === t ? 'primary' : 'default'}
              variant={filterType === t ? 'filled' : 'outlined'}
            />
          ))}
        </Box>
      )}

      {records.length === 0 && !showForm && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography color="text.secondary">Sin consultas registradas.</Typography>
          {canCreate && (
            <Button sx={{ mt: 2 }} variant="outlined" startIcon={<AddIcon />} onClick={() => setShowForm(true)}>
              Crear primera consulta
            </Button>
          )}
        </Box>
      )}

      {filteredRecords.length === 0 && filterType && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
          Sin consultas de tipo "{filterType}".
        </Typography>
      )}

      {filteredRecords.map((r, idx) => {
        const linkedProblem = r.problem_id ? problems.find(p => p.id === r.problem_id) : null;
        return (
        <Accordion
          key={r.id}
          ref={el => { accordionRefs.current[idx] = el; }}
          expanded={expandedIndex === idx}
          onChange={(_, open) => setExpandedIndex(open ? idx : null)}
          disableGutters
          sx={{ mb: 1, borderRadius: '8px !important', '&:before': { display: 'none' },
            border: '1px solid', borderColor: expandedIndex === idx ? 'primary.300' : 'divider' }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, py: 0.5, '& .MuiAccordionSummary-content': { minWidth: 0 } }}>
            {/* flexWrap en xs: fecha + chips + flechas de navegación (cuando
                está expandido) son todos flexShrink:0 — en ~360px no entran
                en una sola fila sin aplastar el texto truncado del medio, así
                que en mobile la navegación cae a su propia línea en vez de
                forzarse. */}
            <Box sx={{ display: 'flex', alignItems: { xs: 'center', sm: 'flex-start' }, gap: 1.5, rowGap: 0.5, flexWrap: { xs: 'wrap', sm: 'nowrap' }, flex: 1, minWidth: 0, py: { sm: 0.75 } }}>
              <Box sx={{ minWidth: 80, flexShrink: 0, pt: { sm: '1px' } }}>
                <Typography variant="caption" color="text.secondary">{fmtDate(r.created_at)}</Typography>
              </Box>
              {r.consultation_type && (
                <Chip label={r.consultation_type} size="small" color="primary" variant="outlined"
                  sx={{ height: 20, fontSize: '0.65rem', flexShrink: 0 }} />
              )}
              {r.status !== 'finalizado' && (
                <Chip label="Borrador" size="small" color="warning"
                  sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, flexShrink: 0 }} />
              )}
              {linkedProblem && (
                <Chip
                  label={linkedProblem.name} size="small"
                  color={linkedProblem.status === 'activo' ? 'error' : linkedProblem.status === 'cronico' ? 'warning' : 'default'}
                  variant="outlined"
                  sx={{
                    height: 20, fontSize: '0.62rem', flexShrink: 0, maxWidth: 120,
                    '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
                  }}
                />
              )}
              <Box sx={{ flex: 1, minWidth: 120, overflow: 'hidden' }}>
                {(r.notes || r.diagnosis || r.chief_complaint) ? (
                  <Typography variant="body2" fontWeight={700} sx={{
                    display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
                    overflow: 'hidden', lineHeight: 1.35, whiteSpace: 'pre-line',
                  }}>
                    {recordSummary(r.notes || r.diagnosis || r.chief_complaint)}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.disabled" noWrap>Sin evolución cargada</Typography>
                )}
              </Box>
              {/* Navigation arrows — only shown when this accordion is expanded */}
              {expandedIndex === idx && filteredRecords.length > 1 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, ml: { xs: 0, sm: 1 }, flexShrink: 0 }}
                  onClick={e => e.stopPropagation()}>
                  <IconButton size="small" disabled={idx === 0} onClick={() => handleNav(idx - 1)}>
                    <ChevronLeftIcon fontSize="small" />
                  </IconButton>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 42, textAlign: 'center' }}>
                    {idx + 1} / {filteredRecords.length}
                  </Typography>
                  <IconButton size="small" disabled={idx === filteredRecords.length - 1} onClick={() => handleNav(idx + 1)}>
                    <ChevronRightIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 1 }}>
            <RecordFullDetail record={r} onSaved={onRecordSaved} patientId={patient.id} />
          </AccordionDetails>
        </Accordion>
        );
      })}

      {/* Solo mientras se mira la lista completa sin filtrar — cargar más
          bajo un filtro de tipo/problema activo confundiría (traería
          registros nuevos a la lista completa que el filtro recién
          aplicaría después, sin ningún indicio visible de que pasó). */}
      {hasMoreRecords && !filterType && !activeProblem && (
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Button variant="outlined" size="small" disabled={loadingMoreRecords} onClick={onLoadMoreRecords}>
            {loadingMoreRecords ? <CircularProgress size={16} /> : 'Cargar más consultas'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

// ── EstudiosTab ───────────────────────────────────────────────────────────

const EMPTY_STUDY_FORM = { category: 'laboratorio', description: '', study_date: '' };

const EstudiosTab = ({ patient }) => {
  const { isAdmin, isMedico } = useAuth();
  const notify = useNotification();
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterCat, setFilterCat] = useState('todos');
  const [showForm, setShowForm] = useState(false);
  const [studyForm, setStudyForm] = useState(EMPTY_STUDY_FORM);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [viewerIndex, setViewerIndex] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState('');
  const canManage = isAdmin || isMedico;

  const load = useCallback(async () => {
    setLoading(true);
    try { setStudies((await getPatientStudies(patient.id)).data || []); } catch { }
    finally { setLoading(false); }
  }, [patient.id]);

  useEffect(() => { load(); }, [load]);

  const filtered = filterCat === 'todos' ? studies : studies.filter(s => s.category === filterCat);
  // RecordFileViewer distingue _kind ('study' vs 'file') para saber qué
  // endpoint de descarga usar — acá todos son estudios de paciente.
  const viewableStudies = filtered
    .filter(s => isPreviewable(s.mime_type))
    .map(s => ({ ...s, _kind: 'study' }));
  const handlePreview = (study) => setViewerIndex(viewableStudies.findIndex(s => s.id === study.id));

  // Agrupa por group_id manteniendo el orden ya devuelto por el backend
  // (study_date/created_at desc) — la posición del primer archivo de cada
  // grupo encontrado define dónde aparece la tarjeta en la lista.
  const studyGroups = useMemo(() => {
    const groups = [];
    const byGroupId = new Map();
    for (const s of filtered) {
      if (s.group_id && byGroupId.has(s.group_id)) {
        byGroupId.get(s.group_id).files.push(s);
      } else if (s.group_id) {
        const g = { group_id: s.group_id, category: s.category, description: s.description, study_date: s.study_date, files: [s] };
        byGroupId.set(s.group_id, g);
        groups.push(g);
      } else {
        groups.push({ group_id: null, files: [s] });
      }
    }
    return groups;
  }, [filtered]);

  // Varios archivos a la vez comparten la misma categoría/descripción/fecha
  // del formulario (ej. imágenes + informe de una misma ecografía) — mismo
  // patrón ya usado para adjuntos de HC. Además comparten un group_id para
  // que se muestren agrupados como UN estudio en vez de filas sueltas sin
  // relación entre sí. Pedido explícito del usuario 2026-10-27/28.
  const handleUpload = async () => {
    if (pendingFiles.length === 0) { setUploadErr('Seleccioná al menos un archivo'); return; }
    setUploading(true); setUploadErr('');
    const failed = [];
    const groupId = pendingFiles.length > 1 ? crypto.randomUUID().replace(/-/g, '') : null;
    for (const file of pendingFiles) {
      try { await uploadPatientStudy(patient.id, file, { ...studyForm, group_id: groupId }); }
      catch (err) { failed.push(file.name); }
    }
    await load();
    if (failed.length > 0) {
      setUploadErr(`No se pudieron subir: ${failed.join(', ')}`);
    } else {
      setShowForm(false); setStudyForm(EMPTY_STUDY_FORM); setPendingFiles([]);
    }
    setUploading(false);
  };

  // Agregar un archivo más a un estudio ya guardado (ej. el informe llega
  // unos días después de las imágenes) — el backend crea el group_id en el
  // momento si el estudio todavía era de un solo archivo suelto.
  const handleAddToGroup = async (group, file) => {
    setUploadErr('');
    try {
      await addFileToStudyGroup(group.files[0].id, file);
      await load();
    } catch (err) {
      setUploadErr(err.userMessage || 'No se pudo agregar el archivo');
    }
  };

  // El backend propaga descripción/categoría/fecha a todo el group_id al
  // editar un archivo — acá se refleja localmente lo mismo, por eso
  // handleRename recibe el group_id (o el id del archivo si está suelto)
  // y actualiza todos los PatientFile que lo comparten.
  const handleRename = (groupIdOrFileId, newName) => {
    setStudies(prev => prev.map(s =>
      (s.group_id === groupIdOrFileId || (!s.group_id && s.id === groupIdOrFileId))
        ? { ...s, description: newName } : s
    ));
  };

  const deleteConfirm = useConfirmDialog();

  const confirmDelete = async (study) => {
    deleteConfirm.close();
    try { await deletePatientStudy(study.id); setStudies(prev => prev.filter(s => s.id !== study.id)); }
    catch (err) { notify(err.userMessage || 'Error al eliminar el estudio', 'error'); }
  };

  // Un médico real confundió estos chips de filtro con el Select de
  // "Categoría" del formulario de subida de más abajo — mismo texto
  // ("Informe", "Laboratorio"...) en la misma pantalla, uno encima del
  // otro, e hizo click en el chip pensando que elegía la categoría del
  // archivo nuevo. El filtro solo sirve para algo cuando hay estudios de
  // VARIAS categorías distintas cargados — con pocos estudios (o todos de
  // la misma categoría) no hay nada que filtrar, así que ahí se ocultan
  // del todo: la pantalla va directo al botón "Agregar estudio" + lista,
  // sin un control de "categoría" visible que pueda competir con el del
  // formulario. Pedido explícito del usuario, caso real reportado 2026-10-07.
  const categoriesWithStudies = new Set(studies.map(s => s.category)).size;
  const showCategoryFilter = categoriesWithStudies > 1;

  return (
    <Box>
      {showCategoryFilter && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Filtrar por categoría:
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            {[{ key: 'todos', label: `Todos (${studies.length})`, count: studies.length },
              ...Object.entries(STUDY_CATEGORIES).map(([key, c]) => {
                const count = studies.filter(s => s.category === key).length;
                return { key, label: `${c.label} (${count})`, count };
              })
            ].filter(({ key, count }) => key === 'todos' || count > 0 || filterCat === key)
            .map(({ key, label }) => (
              <Chip
                key={key}
                label={label}
                size="small"
                onClick={() => setFilterCat(key)}
                color={filterCat === key ? 'primary' : 'default'}
                variant={filterCat === key ? 'filled' : 'outlined'}
                sx={{ fontSize: '0.72rem' }}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Formulario de nuevo estudio */}
      {canManage && (
        <Box sx={{ mb: 2 }}>
          {!showForm ? (
            <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={() => setShowForm(true)}>
              Agregar estudio
            </Button>
          ) : (
            <Paper variant="outlined" sx={{ p: 2, borderColor: 'primary.300', borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>Nuevo estudio complementario</Typography>
              {uploadErr && <Alert severity="error" sx={{ mb: 1.5 }}>{uploadErr}</Alert>}
              <Grid container spacing={1.5}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Categoría</InputLabel>
                    <Select value={studyForm.category} label="Categoría"
                      onChange={e => setStudyForm(p => ({ ...p, category: e.target.value }))}>
                      {Object.entries(STUDY_CATEGORIES).map(([k, c]) => (
                        <MenuItem key={k} value={k}>{c.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField fullWidth size="small" label="Descripción (ej: Hemograma completo)"
                    value={studyForm.description}
                    onChange={e => setStudyForm(p => ({ ...p, description: e.target.value }))} />
                </Grid>
                {/* Antes "Fecha del estudio" en sm:3 (1/4 del ancho) quedaba
                    apretado: label largo + ícono de calendario del input
                    nativo no entraban bien, se veía cortado. Label más
                    corto y más ancho relativo — reportado por el usuario
                    2026-10-12 con captura. */}
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField fullWidth size="small" type="date" label="Fecha"
                    InputLabelProps={{ shrink: true }} value={studyForm.study_date}
                    onChange={e => setStudyForm(p => ({ ...p, study_date: e.target.value }))} />
                </Grid>
              </Grid>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.5, flexWrap: 'wrap' }}>
                <AttachFileButton
                  onFilesSelected={(files) => setPendingFiles(files)}
                  onFilesRejected={setUploadErr}
                  multiple
                  variant="outlined" size="small"
                  label={pendingFiles.length > 0
                    ? (pendingFiles.length === 1 ? pendingFiles[0].name : `${pendingFiles.length} archivos seleccionados`)
                    : 'Seleccionar archivo(s) *'}
                />
                {pendingFiles.length === 1 && (
                  <Typography variant="caption" color="text.secondary">
                    {formatBytes(pendingFiles[0].size)}
                  </Typography>
                )}
                <Box sx={{ flex: 1 }} />
                <Button size="small" onClick={() => { setShowForm(false); setPendingFiles([]); setUploadErr(''); }}>
                  Cancelar
                </Button>
                <Button variant="contained" size="small" onClick={handleUpload} disabled={uploading || pendingFiles.length === 0}>
                  {uploading ? <><CircularProgress size={14} sx={{ mr: 0.75 }} />Subiendo...</> : (pendingFiles.length > 1 ? `Guardar estudio (${pendingFiles.length} archivos)` : 'Guardar estudio')}
                </Button>
              </Box>
            </Paper>
          )}
        </Box>
      )}

      {/* Lista de estudios — los que compartan group_id se muestran agrupados
          como UN estudio (StudyGroupCard); el resto, sueltos (StudyRow). */}
      {loading ? (
        <CircularProgress size={24} sx={{ display: 'block', mx: 'auto', mt: 3 }} />
      ) : filtered.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 5 }}>
          <Typography color="text.secondary">
            {filterCat === 'todos' ? 'Sin estudios cargados para este paciente.' : `Sin estudios de tipo "${STUDY_CATEGORIES[filterCat]?.label}".`}
          </Typography>
        </Box>
      ) : (
        <Stack spacing={0.75}>
          {studyGroups.map(g => g.group_id ? (
            <StudyGroupCard key={g.group_id} group={g}
              onDelete={canManage ? deleteConfirm.open : null}
              onPreview={handlePreview}
              onRename={canManage ? handleRename : null}
              onAddFile={canManage ? handleAddToGroup : null}
            />
          ) : (
            <StudyRow key={g.files[0].id} study={g.files[0]} onDelete={canManage ? deleteConfirm.open : null} onPreview={handlePreview} onRename={canManage ? handleRename : null} />
          ))}
        </Stack>
      )}

      {viewerIndex !== null && (
        <RecordFileViewer
          files={viewableStudies}
          index={viewerIndex}
          onIndexChange={setViewerIndex}
          onClose={() => setViewerIndex(null)}
          onDownload={(f) => downloadPatientStudy(f.id).then((res) => downloadBlobAsFile(res.data, f.original_filename, f.mime_type))}
        />
      )}

      <ConfirmDialog
        open={!!deleteConfirm.pending}
        title="Eliminar estudio"
        message={`Eliminar "${deleteConfirm.pending?.original_filename}"?`}
        onCancel={deleteConfirm.close}
        onConfirm={() => confirmDelete(deleteConfirm.pending)}
      />
    </Box>
  );
};

export { ResumenTab, ConsultasTab, EstudiosTab };
